import React, { useState, useEffect, useRef, useMemo } from 'react';
import Swal from 'sweetalert2';
import { authService } from '../api/auth';
import {
  obtenerCristales,
  obtenerArmazones,
  obtenerLentesContacto,
  obtenerProductos,
  agregarProducto,
  editarProducto,
  eliminarProducto,
  agregarCristal,
  editarCristal,
  agregarArmazon,
  editarArmazon,
  agregarLenteContacto,
  editarLenteContacto,
  guardarDescuentoEfectivo,
  guardarDescuentoArmazones,
  guardarCuotasOptica,
  obtenerCatalogosCristales,
  obtenerCatalogosArmazones,
  crearCatalogoCristalesItem,
  actualizarCatalogoCristalesItem,
  desactivarCatalogoCristalesItem,
  eliminarCatalogoCristalesItem,
  detectarDuplicadosCatalogoCristales,
} from '../api/backend_v2';
import { MagnifyingGlassIcon, PencilIcon, EyeIcon, ChartBarIcon, PlusIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon, BanknotesIcon, CurrencyDollarIcon, BeakerIcon, SunIcon, Squares2X2Icon, ViewColumnsIcon, BriefcaseIcon, EyeDropperIcon, RectangleGroupIcon, ArchiveBoxIcon, ListBulletIcon, ArrowPathIcon, DocumentDuplicateIcon, SwatchIcon, PrinterIcon, CreditCardIcon, TagIcon, SparklesIcon, TruckIcon } from '@heroicons/react/24/outline';
import { cargarDescuentoEfectivoSeguro, DESCUENTO_EFECTIVO_DEFAULT, cargarDescuentoArmazonesSeguro, DESCUENTO_ARMAZONES_DEFAULT, redondearMiles, formatearTextoCuotas, cargarCuotasOpticaSeguro, OPCIONES_CUOTAS_SISTEMA } from '../utils/precios';
import { obtenerProveedores } from '../utils/proveedores';
import ModalPoliticaDescuentos from '../components/ModalPoliticaDescuentos';
import MercadoPagoConfigModal from '../components/MercadoPagoConfigModal';

// ── helpers ────────────────────────────────────────────────────────────────
const formatearPrecio = (precio) => {
  if (!precio || precio === 0) return '$0';
  return `$${Math.round(parseFloat(precio)).toLocaleString('es-AR')}`;
};

const formatearIndice = (indice) => {
  if (indice == null || indice === '') return '-';
  const numero = parseFloat(String(indice).replace(',', '.'));
  if (Number.isNaN(numero)) return String(indice);
  return numero.toFixed(2);
};

const normalizar = (valor) => String(valor ?? '').toLowerCase().trim();

const parsearBusqueda = (texto) => {
  const q = normalizar(texto);
  if (!q) return [];
  return q
    .split(/\s+/)
    .map(token => {
      const idx = token.indexOf(':');
      if (idx > 0 && idx < token.length - 1) {
        return { campo: token.slice(0, idx), valor: token.slice(idx + 1) };
      }
      return { campo: null, valor: token };
    })
    .filter(t => t.valor);
};

const coincideBusqueda = (tokens, valoresLibres, campos = {}) => {
  if (!tokens || tokens.length === 0) return true;

  const libres = valoresLibres.map(normalizar);
  const normalizarCampo = (campo) => {
    const candidatos = campos[campo] || [];
    return candidatos.map(normalizar);
  };

  return tokens.every(({ campo, valor }) => {
    if (!campo) return libres.some(v => v.includes(valor));
    return normalizarCampo(campo).some(v => v.includes(valor));
  });
};

// Colores predefinidos disponibles en el modal de administración
const COLOR_PRESETS = [
  { key: 'azul-oscuro',  label: 'Azul oscuro',  even: 'bg-blue-100',   odd: 'bg-blue-200',   dot: 'bg-blue-500'    },
  { key: 'celeste',      label: 'Celeste',       even: 'bg-sky-50',     odd: 'bg-sky-100',    dot: 'bg-sky-400'     },
  { key: 'rojo',         label: 'Rojo',          even: 'bg-red-50',     odd: 'bg-red-100',    dot: 'bg-red-500'     },
  { key: 'verde',        label: 'Verde',         even: 'bg-green-50',   odd: 'bg-green-100',  dot: 'bg-green-500'   },
  { key: 'amarillo',     label: 'Amarillo',      even: 'bg-yellow-50',  odd: 'bg-yellow-100', dot: 'bg-yellow-400'  },
  { key: 'naranja',      label: 'Naranja',       even: 'bg-orange-50',  odd: 'bg-orange-100', dot: 'bg-orange-500'  },
  { key: 'violeta',      label: 'Violeta',       even: 'bg-purple-50',  odd: 'bg-purple-100', dot: 'bg-purple-500'  },
  { key: 'rosa',         label: 'Rosa',          even: 'bg-pink-50',    odd: 'bg-pink-100',   dot: 'bg-pink-400'    },
  { key: 'teal',         label: 'Teal',          even: 'bg-teal-50',    odd: 'bg-teal-100',   dot: 'bg-teal-500'    },
  { key: 'gris',         label: 'Gris',          even: 'bg-gray-50',    odd: 'bg-gray-100',   dot: 'bg-gray-400'    },
];

const DEFAULT_MARCA_COLORES = {
  'novar':     { even: 'bg-blue-100', odd: 'bg-blue-200', preset: 'azul-oscuro' },
  'varilux':   { even: 'bg-sky-50',   odd: 'bg-sky-100',  preset: 'celeste'     },
  'amplitude': { even: 'bg-red-50',   odd: 'bg-red-100',  preset: 'rojo'        },
};

// Mapeo de clases Tailwind a valores hex para impresión
const TAILWIND_HEX = {
  'bg-white':       '#ffffff',
  'bg-blue-50':     '#eff6ff',  'bg-blue-100':    '#dbeafe',  'bg-blue-200':    '#bfdbfe',
  'bg-sky-50':      '#f0f9ff',  'bg-sky-100':     '#e0f2fe',
  'bg-red-50':      '#fef2f2',  'bg-red-100':     '#fee2e2',
  'bg-green-50':    '#f0fdf4',  'bg-green-100':   '#dcfce7',
  'bg-emerald-50':  '#ecfdf5',  'bg-emerald-100': '#d1fae5',
  'bg-yellow-50':   '#fefce8',  'bg-yellow-100':  '#fef9c3',
  'bg-orange-50':   '#fff7ed',  'bg-orange-100':  '#ffedd5',
  'bg-purple-50':   '#faf5ff',  'bg-purple-100':  '#f3e8ff',
  'bg-pink-50':     '#fdf2f8',  'bg-pink-100':    '#fce7f3',
  'bg-teal-50':     '#f0fdfa',  'bg-teal-100':    '#ccfbf1',
  'bg-cyan-50':     '#ecfeff',  'bg-cyan-100':    '#cffafe',
  'bg-gray-50':     '#f9fafb',  'bg-gray-100':    '#f3f4f6',
};

const getRowClassMarca = (marcaColores, marca, index, fallbackEven, fallbackOdd) => {
  const colores = marcaColores[normalizar(marca)];
  if (colores) return index % 2 === 0 ? colores.even : colores.odd;
  return index % 2 === 0 ? fallbackEven : fallbackOdd;
};

function Paginador({ page, totalPages, total, pageSize, setPage }) {
  if (totalPages <= 1) return null;
  const from = page * pageSize + 1;
  const to = Math.min((page + 1) * pageSize, total);
  const windowSize = Math.min(totalPages, 5);
  let startPage = Math.max(0, page - Math.floor(windowSize / 2));
  if (startPage + windowSize > totalPages) startPage = totalPages - windowSize;
  return (
    <div className="flex items-center justify-between px-6 py-2 bg-gray-50 border-t border-gray-200 text-xs">
      <span className="text-gray-500">{from}–{to} de {total}</span>
      <div className="flex items-center gap-1">
        <button onClick={() => setPage(0)} disabled={page === 0} className="px-2 py-1 rounded border border-gray-300 disabled:opacity-30 hover:bg-gray-200">«</button>
        <button onClick={() => setPage(page - 1)} disabled={page === 0} className="px-2 py-1 rounded border border-gray-300 disabled:opacity-30 hover:bg-gray-200">‹</button>
        {Array.from({ length: windowSize }, (_, i) => startPage + i).map(p => (
          <button key={p} onClick={() => setPage(p)}
            className={`px-2 py-1 rounded border ${
              p === page ? 'bg-blue-500 text-white border-blue-500' : 'border-gray-300 hover:bg-gray-200'
            }`}
          >{p + 1}</button>
        ))}
        <button onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1} className="px-2 py-1 rounded border border-gray-300 disabled:opacity-30 hover:bg-gray-200">›</button>
        <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1} className="px-2 py-1 rounded border border-gray-300 disabled:opacity-30 hover:bg-gray-200">»</button>
      </div>
    </div>
  );
}

function sortBy(arr, key, dir) {
  if (!key) return arr;
  return [...arr].sort((a, b) => {
    const va = a[key] ?? '';
    const vb = b[key] ?? '';
    let result = typeof va === 'number' && typeof vb === 'number'
      ? va - vb
      : String(va).localeCompare(String(vb), 'es', { sensitivity: 'base' });

    if (result === 0) {
      // Desempate secundario por stock si aplica
      if (a.cantidad !== undefined && b.cantidad !== undefined) {
        const aStock = parseInt(a.cantidad, 10) > 0 ? 0 : 1;
        const bStock = parseInt(b.cantidad, 10) > 0 ? 0 : 1;
        if (aStock !== bStock) return aStock - bStock;
      }
      // Desempate secundario por modelo o producto
      const subA = a.modelo || a.producto || '';
      const subB = b.modelo || b.producto || '';
      if (subA || subB) {
        const subResult = String(subA).localeCompare(String(subB), 'es', { sensitivity: 'base' });
        if (subResult !== 0) return subResult;
      }
    }
    return dir === 'asc' ? result : -result;
  });
}

function SortIcon({ col, sortCol, sortDir }) {
  if (sortCol !== col) return <ChevronUpIcon className="h-3 w-3 opacity-30 inline ml-1" />;
  return sortDir === 'asc'
    ? <ChevronUpIcon className="h-3 w-3 inline ml-1" />
    : <ChevronDownIcon className="h-3 w-3 inline ml-1" />;
}

const ListaPrecios = () => {
  // datos
  const [cristales, setCristales] = useState([]);
  const [armazones, setArmazones] = useState([]);
  const [lentesContacto, setLentesContacto] = useState([]);
  const [productos, setProductos] = useState([]);
  const [proveedores, setProveedores] = useState([]);

  // UI general
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroCategoria, setFiltroCategoria] = useState('todos');
  const [busquedaGlobal, setBusquedaGlobal] = useState('');
  const [busquedaArmazones, setBusquedaArmazones] = useState('');
  const [paginaArmazones, setPaginaArmazones] = useState(0);
  const [tamanoPaginaArmazones, setTamanoPaginaArmazones] = useState(12);
  const [hoverStockArmazonId, setHoverStockArmazonId] = useState(null);
  const [touchStockArmazonId, setTouchStockArmazonId] = useState(null);
  const [actualizandoStockArmazonId, setActualizandoStockArmazonId] = useState(null);
  const _opticaId = authService.obtenerUsuarioActual()?.optica_id ?? 'default';
  const _descuentoKey = `optica_${_opticaId}_descuentoEfectivo`;
  const [descuentoEfectivo, setDescuentoEfectivo] = useState(() => {
    const guardado = localStorage.getItem(_descuentoKey);
    return guardado !== null ? parseFloat(guardado) : DESCUENTO_EFECTIVO_DEFAULT;
  });
  const _descuentoArmazonesKey = `optica_${_opticaId}_descuentoArmazones`;
  const [descuentoArmazones, setDescuentoArmazones] = useState(() => {
    const guardado = localStorage.getItem(_descuentoArmazonesKey);
    return guardado !== null ? parseFloat(guardado) : DESCUENTO_ARMAZONES_DEFAULT;
  });
  const _descuentoArmazonesDebounce = useRef(null);

  const [cuotasDisponibles, setCuotasDisponibles] = useState([]);
  const [modalPoliticaOpen, setModalPoliticaOpen] = useState(false);
  const [modalMPOpen, setModalMPOpen] = useState(false);
  const _cuotasDebounce = useRef(null);

  const toggleCuotaOptica = (cuotaNum) => {
    const next = cuotasDisponibles.includes(cuotaNum)
      ? cuotasDisponibles.filter((c) => c !== cuotaNum)
      : [...cuotasDisponibles, cuotaNum].sort((a, b) => a - b);
    setCuotasDisponibles(next);
    if (_cuotasDebounce.current) clearTimeout(_cuotasDebounce.current);
    _cuotasDebounce.current = setTimeout(() => {
      guardarCuotasOptica(next, next.length ? Math.max(...next) : null).catch(() => {});
    }, 600);
  };

  // Filtros adicionales
  const [filtroMarca, setFiltroMarca] = useState('');
  const [filtroTipoCristal, setFiltroTipoCristal] = useState('');
  const [filtroLaboratorio, setFiltroLaboratorio] = useState('');

  // Ordenamiento por sección (por defecto organizados por marca ascendente)
  const [sortCristales, setSortCristales] = useState({ col: 'marca', dir: 'asc' });
  const [sortArmazones, setSortArmazones] = useState({ col: 'marca', dir: 'asc' });
  const [sortLentes, setSortLentes] = useState({ col: 'marca', dir: 'asc' });
  const [sortProductos, setSortProductos] = useState({ col: '', dir: 'asc' });

  // Edición de precio
  const [editando, setEditando] = useState(false);
  const [itemEditando, setItemEditando] = useState(null);
  const [tipoItemEditando, setTipoItemEditando] = useState('');
  const [precioEdicion, setPrecioEdicion] = useState('');
  const [precioEfectivoEdicion, setPrecioEfectivoEdicion] = useState('');

  // Productos: nuevo / edición
  const [modalProducto, setModalProducto] = useState(false);
  const [productoForm, setProductoForm] = useState({ tipo: '', producto: '', precio_lista: '', precio_efectivo: '' });
  const [productoEditandoId, setProductoEditandoId] = useState(null);
  const [guardandoProducto, setGuardandoProducto] = useState(false);

  // Modal agregar/editar cristal/armazon/lente rápido
  const [modalItem, setModalItem] = useState(false);
  const [modalItemTipo, setModalItemTipo] = useState(''); // 'cristal' | 'armazon' | 'lente'
  const [modalItemId, setModalItemId] = useState(null);
  const [modalItemForm, setModalItemForm] = useState({});
  const [guardandoItem, setGuardandoItem] = useState(false);

  // Modal actualización masiva de precios
  const [modalMasivo, setModalMasivo] = useState(false);
  const [masivoSeccion, setMasivoSeccion] = useState('cristales');
  const [masivoMarca, setMasivoMarca] = useState('');
  const [masivoSeleccionados, setMasivoSeleccionados] = useState(new Set());
  const [masivoModo, setMasivoModo] = useState('marca'); // 'marca' | 'seleccion'
  const [masivoPorcentaje, setMasivoPorcentaje] = useState('');
  const [masivoTipoPrecio, setMasivoTipoPrecio] = useState('lista'); // 'lista' | 'efectivo' | 'ambos'
  const [aplicandoMasivo, setAplicandoMasivo] = useState(false);

  // Colores de marcas (persistidos en localStorage)
  const [marcaColores, setMarcaColores] = useState(() => {
    try {
      const s = localStorage.getItem('optica_marcaColores');
      return s ? JSON.parse(s) : { ...DEFAULT_MARCA_COLORES };
    } catch { return { ...DEFAULT_MARCA_COLORES }; }
  });
  const [modalColores, setModalColores] = useState(false);
  const [colorForm, setColorForm] = useState({ marca: '', presetKey: 'azul-oscuro' });
  const [colorEditando, setColorEditando] = useState(null);

  // Catálogos normalizados (cristales/armazones)
  const [catalogos, setCatalogos] = useState({
    laboratorios: [],
    marcas_cristales: [],
    productos_cristales: [],
    materiales_cristales: [],
    tratamientos: [],
    marcas_armazones: [],
    material_armazon: [],
    color_armazon: [],
  });
  const [modalCatalogos, setModalCatalogos] = useState(false);
  const [catalogoActivo, setCatalogoActivo] = useState('productos_cristales');
  const [nuevoCatalogoNombre, setNuevoCatalogoNombre] = useState('');
  const [guardandoCatalogo, setGuardandoCatalogo] = useState(false);
  const [nuevoLaboratorioDireccion, setNuevoLaboratorioDireccion] = useState('');
  const [nuevoLaboratorioTelefonos, setNuevoLaboratorioTelefonos] = useState('');
  const [nuevoMaterialRango, setNuevoMaterialRango] = useState('');
  const [catalogoBusqueda, setCatalogoBusqueda] = useState('');
  const [catalogoEstadoFiltro, setCatalogoEstadoFiltro] = useState('todos'); // todos | activos | inactivos
  const [duplicadosCatalogo, setDuplicadosCatalogo] = useState([]);
  const [cargandoDuplicados, setCargandoDuplicados] = useState(false);

  const [modoNuevoCatalogoCampo, setModoNuevoCatalogoCampo] = useState({
    cristalMarca: false,
    cristalProducto: false,
    cristalMaterial: false,
    cristalLaboratorio: false,
    cristalTratamiento: false,
    armazonMarca: false,
  });

  // Modal imprimir / exportar
  const [modalImprimir, setModalImprimir] = useState(false);
  const [imprimirOpciones, setImprimirOpciones] = useState({
    monofocal: true, bifocal: true, multifocal: true, ocupacional: true,
    lentes: true, armazones: true, productos: true,
  });

  // Paginación de cristales
  const [pageSize, setPageSize] = useState(10);
  const [cristalPages, setCristalPages] = useState({});

  // ── carga de datos ──────────────────────────────────────────────────────
  useEffect(() => { cargarDatos(); }, []);

  // Cargar descuento efectivo desde la API al montar (la BD es la fuente de verdad)
  useEffect(() => {
    cargarDescuentoEfectivoSeguro(descuentoEfectivo)
      .then((value) => {
        setDescuentoEfectivo(value);
        localStorage.setItem(_descuentoKey, String(value));
      })
      .catch(() => { /* usa el valor de localStorage como fallback */ });
  }, []); // eslint-disable-line

  // Persistir colores de marcas en localStorage
  useEffect(() => {
    localStorage.setItem('optica_marcaColores', JSON.stringify(marcaColores));
  }, [marcaColores]);

  // Resetear paginación al cambiar filtros o tamaño de página
  useEffect(() => {
    setCristalPages({});
  }, [filtroTipoCristal, filtroMarca, filtroLaboratorio, busquedaGlobal, pageSize]);

  // ── handlers colores de marcas ──────────────────────────────────────────
  const handleGuardarColor = () => {
    const marcaKey = normalizar(colorForm.marca);
    if (!marcaKey) return;
    const preset = COLOR_PRESETS.find(p => p.key === colorForm.presetKey) || COLOR_PRESETS[0];
    setMarcaColores(prev => ({
      ...prev,
      [marcaKey]: { even: preset.even, odd: preset.odd, preset: preset.key },
    }));
    setColorForm({ marca: '', presetKey: 'azul-oscuro' });
    setColorEditando(null);
  };

  const handleEliminarColor = (marcaKey) => {
    setMarcaColores(prev => {
      const next = { ...prev };
      delete next[marcaKey];
      return next;
    });
  };

  const handleEditarColor = (marcaKey) => {
    const colores = marcaColores[marcaKey];
    setColorForm({ marca: marcaKey, presetKey: colores.preset || 'azul-oscuro' });
    setColorEditando(marcaKey);
  };

  const setCristalPage = (key, p) =>
    setCristalPages(prev => ({ ...prev, [key]: p }));

  const cargarDatos = async () => {
    setLoading(true);
    setError(null);
    try {
      const [resCristales, resArmazones, resLentes, resProductos] = await Promise.all([
        obtenerCristales(),
        obtenerArmazones(),
        obtenerLentesContacto(),
        obtenerProductos(),
      ]);
      setCristales(resCristales.cristales || []);
      setArmazones(resArmazones.armazones || []);
      setLentesContacto(resLentes.lentes || []);
      setProductos(resProductos.productos || []);

      // Cargar configuración comercial de la óptica (descuentos y cuotas)
      cargarDescuentoEfectivoSeguro().then((v) => {
        setDescuentoEfectivo(v);
        localStorage.setItem(_descuentoKey, String(v));
      }).catch(() => {});
      cargarDescuentoArmazonesSeguro().then((v) => {
        setDescuentoArmazones(v);
        localStorage.setItem(_descuentoArmazonesKey, String(v));
      }).catch(() => {});
      cargarCuotasOpticaSeguro().then((res) => {
        setCuotasDisponibles(res.cuotas_disponibles || []);
      }).catch(() => {});
      
      // Cargar proveedores
      try {
        const resProveedores = await obtenerProveedores();
        setProveedores(resProveedores);
      } catch {
        // Si los proveedores no cargan, mantener estado vacío
      }
      
      try {
        const resCatalogos = await obtenerCatalogosCristales(false);
        const resCatalogosArmazones = await obtenerCatalogosArmazones(false);
        setCatalogos({
          laboratorios: resCatalogos.laboratorios || [],
          marcas_cristales: resCatalogos.marcas_cristales || [],
          productos_cristales: resCatalogos.productos_cristales || [],
          materiales_cristales: resCatalogos.materiales_cristales || [],
          tratamientos: resCatalogos.tratamientos || [],
          marcas_armazones: resCatalogos.marcas_armazones || [],
          material_armazon: resCatalogosArmazones.material_armazon || [],
          color_armazon: resCatalogosArmazones.color_armazon || [],
        });
      } catch {
        // Si los catálogos aún no existen en el entorno, mantenemos la UI actual.
      }
    } catch (err) {
      setError('Error al cargar los datos: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── listas únicas para filtros ──────────────────────────────────────────
  const marcasUnicas = useMemo(() => {
    const set = new Set();
    cristales.forEach(c => c.marca && set.add(c.marca));
    armazones.forEach(a => a.marca && set.add(a.marca));
    lentesContacto.forEach(l => l.marca && set.add(l.marca));
    return [...set].sort();
  }, [cristales, armazones, lentesContacto]);

  // Obtener proveedor por nombre de marca
  const obtenerProveedorPorMarca = (nombreMarca) => {
    if (!nombreMarca) return null;
    const marcaNormalizada = normalizar(nombreMarca);
    for (const proveedor of proveedores) {
      if (proveedor.marcas && Array.isArray(proveedor.marcas)) {
        const tieneMarca = proveedor.marcas.some(
          (m) => normalizar(m.nombre) === marcaNormalizada
        );
        if (tieneMarca) return proveedor.id;
      }
    }
    return null;
  };

  const tiposCristalUnicos = useMemo(() => {
    return [...new Set(cristales.map(c => c.tipo_vision).filter(Boolean))].sort();
  }, [cristales]);

  const normalizarLaboratorio = (valor) => {
    const limpio = String(valor ?? '').trim();
    return limpio || 'Sin laboratorio';
  };

  const laboratoriosUnicos = useMemo(() => {
    return [...new Set(cristales.map(c => normalizarLaboratorio(c.laboratorio)).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'es'));
  }, [cristales]);

  // ── helpers de coincidencia ─────────────────────────────────────────────
  const termGlobalTokens = useMemo(() => parsearBusqueda(busquedaGlobal), [busquedaGlobal]);
  const termArmazonTokens = useMemo(() => parsearBusqueda(busquedaArmazones), [busquedaArmazones]);

  const matchCristal = (c, tokens) => coincideBusqueda(
    tokens,
    [
      c.tipo_vision,
      c.marca,
      c.producto,
      c.material,
      c.rango,
      c.stock ? 'stock' : 'laboratorio',
      c.tratamiento || (c.antirreflejo ? 'antirreflejo' : 'sin tratamiento'),
      c.precio_publico,
      c.precio_efectivo,
    ],
    {
      tipo: [c.tipo_vision],
      vision: [c.tipo_vision],
      marca: [c.marca],
      producto: [c.producto],
      material: [c.material],
      rango: [c.rango],
      stock: [c.stock ? 'stock' : 'laboratorio'],
      tratamiento: [c.tratamiento || (c.antirreflejo ? 'antirreflejo' : 'sin tratamiento')],
      precio: [c.precio_publico, c.precio_efectivo],
    }
  );

  const matchLente = (l, tokens) => coincideBusqueda(
    tokens,
    [
      l.tipo_vision,
      l.marca,
      l.rango,
      l.color,
      l.son_esteticas ? 'estetica' : 'no estetica',
      l.precio_publico,
      l.precio_efectivo,
    ],
    {
      tipo: [l.tipo_vision],
      vision: [l.tipo_vision],
      marca: [l.marca],
      rango: [l.rango],
      color: [l.color],
      estetica: [l.son_esteticas ? 'si' : 'no'],
      precio: [l.precio_publico, l.precio_efectivo],
    }
  );

  const matchArmazon = (a, tokens) => coincideBusqueda(
    tokens,
    [
      a.marca,
      a.modelo,
      a.material,
      a.color,
      a.cantidad > 0 ? 'stock' : 'sin stock',
      a.precio_publico,
      a.precio_efectivo,
    ],
    {
      marca: [a.marca],
      modelo: [a.modelo],
      material: [a.material],
      color: [a.color],
      stock: [a.cantidad > 0 ? 'stock' : 'sin stock'],
      precio: [a.precio_publico, a.precio_efectivo],
    }
  );

  const matchProducto = (p, tokens) => coincideBusqueda(
    tokens,
    [p.tipo, p.producto, p.precio_lista, p.precio_efectivo],
    {
      tipo: [p.tipo],
      producto: [p.producto],
      nombre: [p.producto],
      precio: [p.precio_lista, p.precio_efectivo],
    }
  );

  const laboratoriosResumen = useMemo(() => {
    const conteo = new Map();
    cristales
      .filter(c =>
        matchCristal(c, termGlobalTokens) &&
        (!filtroMarca || normalizar(c.marca) === normalizar(filtroMarca)) &&
        (!filtroTipoCristal || c.tipo_vision === filtroTipoCristal)
      )
      .forEach(c => {
        const laboratorio = normalizarLaboratorio(c.laboratorio);
        conteo.set(laboratorio, (conteo.get(laboratorio) || 0) + 1);
      });

    return Array.from(conteo.entries())
      .map(([laboratorio, cantidad]) => ({ laboratorio, cantidad }))
      .sort((a, b) => (b.cantidad - a.cantidad) || a.laboratorio.localeCompare(b.laboratorio, 'es'));
  }, [cristales, termGlobalTokens, filtroMarca, filtroTipoCristal]);

  // ── datos filtrados / ordenados ─────────────────────────────────────────
  const cristalesFiltrados = useMemo(() => {
    return cristales.filter(c =>
      matchCristal(c, termGlobalTokens) &&
      (!filtroMarca || normalizar(c.marca) === normalizar(filtroMarca)) &&
      (!filtroTipoCristal || c.tipo_vision === filtroTipoCristal) &&
      (!filtroLaboratorio || normalizar(normalizarLaboratorio(c.laboratorio)) === normalizar(filtroLaboratorio))
    );
  }, [cristales, termGlobalTokens, filtroMarca, filtroTipoCristal, filtroLaboratorio]);

  const cristalesMonofocales  = sortBy(cristalesFiltrados.filter(c => c.tipo_vision === 'Monofocal'), sortCristales.col, sortCristales.dir);
  const cristalesBifocales    = sortBy(cristalesFiltrados.filter(c => c.tipo_vision === 'Bifocal'), sortCristales.col, sortCristales.dir);
  const cristalesMultifocales = sortBy(cristalesFiltrados.filter(c => c.tipo_vision === 'Multifocal'), sortCristales.col, sortCristales.dir);
  const cristalesOcupacionales = sortBy(cristalesFiltrados.filter(c => c.tipo_vision === 'Ocupacional'), sortCristales.col, sortCristales.dir);

  const lentesContactoFiltrados = useMemo(() => {
    return sortBy(
      lentesContacto.filter(l =>
        matchLente(l, termGlobalTokens) &&
        (!filtroMarca || normalizar(l.marca) === normalizar(filtroMarca))
      ),
      sortLentes.col, sortLentes.dir
    );
  }, [lentesContacto, termGlobalTokens, filtroMarca, sortLentes]);

  const armazonesFiltrados = useMemo(() => {
    const filtrados = armazones.filter(a =>
      matchArmazon(a, termGlobalTokens) &&
      matchArmazon(a, termArmazonTokens) &&
      (!filtroMarca || normalizar(a.marca) === normalizar(filtroMarca))
    );
    
    const colOrden = sortArmazones.col || 'marca';
    return sortBy(filtrados, colOrden, sortArmazones.dir || 'asc');
  }, [armazones, termGlobalTokens, termArmazonTokens, filtroMarca, sortArmazones]);

  const totalPaginasArmazones = Math.max(1, Math.ceil(armazonesFiltrados.length / tamanoPaginaArmazones));

  const armazonesPaginados = useMemo(() => {
    const inicio = paginaArmazones * tamanoPaginaArmazones;
    return armazonesFiltrados.slice(inicio, inicio + tamanoPaginaArmazones);
  }, [armazonesFiltrados, paginaArmazones, tamanoPaginaArmazones]);

  useEffect(() => {
    setPaginaArmazones(0);
  }, [busquedaArmazones, filtroMarca, sortArmazones, filtroCategoria, tamanoPaginaArmazones]);

  useEffect(() => {
    if (paginaArmazones > totalPaginasArmazones - 1) {
      setPaginaArmazones(Math.max(0, totalPaginasArmazones - 1));
    }
  }, [paginaArmazones, totalPaginasArmazones]);

  const productosFiltrados = useMemo(() => {
    return sortBy(
      productos.filter(p => matchProducto(p, termGlobalTokens)),
      sortProductos.col, sortProductos.dir
    );
  }, [productos, termGlobalTokens, sortProductos]);

  const hayBusquedaGlobal = termGlobalTokens.length > 0;

  // ── estadísticas ────────────────────────────────────────────────────────
  const estadisticas = {
    totalCristales: cristales.length,
    totalArmazones: armazones.length,
    totalLentesContacto: lentesContacto.length,
    totalProductos: productos.length,
    monofocales:   cristales.filter(c => c.tipo_vision === 'Monofocal').length,
    multifocales:  cristales.filter(c => c.tipo_vision === 'Multifocal').length,
    bifocales:     cristales.filter(c => c.tipo_vision === 'Bifocal').length,
    ocupacionales: cristales.filter(c => c.tipo_vision === 'Ocupacional').length,
    precioPromedioCristales: cristales.length > 0
      ? cristales.reduce((s, c) => s + (parseFloat(c.precio_publico) || 0), 0) / cristales.length : 0,
    precioPromedioArmazones: armazones.length > 0
      ? armazones.reduce((s, a) => s + (parseFloat(a.precio_publico) || 0), 0) / armazones.length : 0,
  };

  // ── toggle sort ─────────────────────────────────────────────────────────
  const toggleSort = (setter, col) => {
    setter(prev => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }));
  };

  // ── edición de precio ───────────────────────────────────────────────────
  const handleEditarPrecio = (item, tipo) => {
    setItemEditando(item);
    setTipoItemEditando(tipo);
    const lista = item.precio_publico != null ? String(item.precio_publico) : '';
    setPrecioEdicion(lista);
    const tieneEfectivo = item.precio_efectivo != null && item.precio_efectivo !== '';
    const autoEfectivo = lista !== '' && !isNaN(parseFloat(lista))
      ? String(redondearMiles(parseFloat(lista) * (1 - descuentoEfectivo / 100)))
      : '';
    setPrecioEfectivoEdicion(tieneEfectivo ? String(item.precio_efectivo) : autoEfectivo);
    setEditando(true);
  };

  const handleGuardarPrecio = async () => {
    if (!itemEditando) return;
    try {
      const { editarCristal, editarArmazon, editarLenteContacto } = await import('../api/backend_v2');
      const base = {
        ...itemEditando,
        precio_publico: precioEdicion !== '' ? redondearMiles(parseFloat(precioEdicion)) : itemEditando.precio_publico,
        precio_efectivo: precioEfectivoEdicion !== '' ? redondearMiles(parseFloat(precioEfectivoEdicion)) : null,
      };
      if (tipoItemEditando === 'cristal') await editarCristal(itemEditando.id, base);
      else if (tipoItemEditando === 'armazon') await editarArmazon(itemEditando.id, base);
      else if (tipoItemEditando === 'lente_contacto') await editarLenteContacto(itemEditando.id, base);
      await cargarDatos();
      cerrarModalEdicion();
      Swal.fire({ icon: 'success', title: 'Precio actualizado', toast: true, position: 'top-end', showConfirmButton: false, timer: 2000, timerProgressBar: true });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error al actualizar precio', text: err.message });
    }
  };

  const handleAjustarStockArmazonRapido = async (armazon, delta) => {
    const actual = parseInt(armazon.cantidad, 10) || 0;
    const nuevo = Math.max(0, actual + delta);
    if (nuevo === actual) return;

    const parseNumero = (v) => {
      if (v === null || v === undefined || v === '') return null;
      const n = parseFloat(v);
      return Number.isNaN(n) ? null : n;
    };

    const payload = {
      marca: String(armazon.marca || '').trim(),
      modelo: armazon.modelo || '',
      color: armazon.color || '',
      material: armazon.material || '',
      cantidad: nuevo,
      medidas: armazon.medidas || '',
      tipo_bisagra: armazon.tipo_bisagra || '',
      costo: parseNumero(armazon.costo),
      precio_publico: parseNumero(armazon.precio_publico),
      precio_efectivo: parseNumero(armazon.precio_efectivo),
      id_proveedor: armazon.id_proveedor ? parseInt(armazon.id_proveedor) : null,
    };

    setActualizandoStockArmazonId(armazon.id);
    try {
      await editarArmazon(armazon.id, payload);
      setArmazones(prev => prev.map(item => (
        item.id === armazon.id ? { ...item, cantidad: nuevo } : item
      )));
    } catch (e) {
      Swal.fire('Error', e.message, 'error');
    } finally {
      setActualizandoStockArmazonId(null);
    }
  };

  const cerrarModalEdicion = () => {
    setEditando(false);
    setItemEditando(null);
    setTipoItemEditando('');
    setPrecioEdicion('');
    setPrecioEfectivoEdicion('');
  };

  // ── productos: alta / edición / baja ────────────────────────────────────
  const abrirModalNuevoProducto = () => {
    setProductoForm({ tipo: '', producto: '', precio_lista: '', precio_efectivo: '' });
    setProductoEditandoId(null);
    setModalProducto(true);
  };

  const abrirModalEditarProducto = (p) => {
    setProductoForm({
      tipo: p.tipo,
      producto: p.producto,
      precio_lista: p.precio_lista != null ? String(p.precio_lista) : '',
      precio_efectivo: p.precio_efectivo != null ? String(p.precio_efectivo) : '',
    });
    setProductoEditandoId(p.id);
    setModalProducto(true);
  };

  const handleGuardarProducto = async () => {
    if (!productoForm.tipo.trim() || !productoForm.producto.trim()) return;
    setGuardandoProducto(true);
    try {
      const payload = {
        tipo: productoForm.tipo.trim(),
        producto: productoForm.producto.trim(),
        precio_lista: productoForm.precio_lista !== '' ? redondearMiles(parseFloat(productoForm.precio_lista)) : 0,
        precio_efectivo: productoForm.precio_efectivo !== '' ? redondearMiles(parseFloat(productoForm.precio_efectivo)) : null,
      };
      if (productoEditandoId) await editarProducto(productoEditandoId, payload);
      else await agregarProducto(payload);
      await cargarDatos();
      setModalProducto(false);
      Swal.fire({ icon: 'success', title: productoEditandoId ? 'Producto actualizado' : 'Producto agregado', toast: true, position: 'top-end', showConfirmButton: false, timer: 2000, timerProgressBar: true });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error al guardar producto', text: err.message });
    } finally {
      setGuardandoProducto(false);
    }
  };

  const handleEliminarProducto = async (id) => {
    const result = await Swal.fire({
      title: '¿Eliminar producto?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });
    if (!result.isConfirmed) return;
    try {
      await eliminarProducto(id);
      await cargarDatos();
      Swal.fire({ icon: 'success', title: 'Producto eliminado', toast: true, position: 'top-end', showConfirmButton: false, timer: 2000, timerProgressBar: true });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error al eliminar', text: err.message });
    }
  };

  // ── duplicar item ────────────────────────────────────────────────────────
  const handleDuplicarItem = (item, tipo) => {
    setModalItemTipo(tipo);
    setModalItemId(null); // null = crear nuevo
    const toStr = v => v != null ? String(v) : '';
    if (tipo === 'cristal') {
      setModalItemForm({
        tipo_vision: item.tipo_vision || 'Monofocal',
        marca: item.marca || '',
        producto: item.producto || '',
        laboratorio: item.laboratorio || '',
        material: item.material || '',
        tratamiento: item.tratamiento || '',
        tratamiento_id: item.tratamiento_id ?? null,
        rango: item.rango || '',
        indice: toStr(item.indice),
        stock: item.stock ?? true,
        antirreflejo: item.antirreflejo ?? false,
        tiene_seguro: item.tiene_seguro ?? false,
        tiene_promocion: item.tiene_promocion ?? false,
        precio_publico: toStr(item.precio_publico),
        precio_efectivo: toStr(item.precio_efectivo),
      });
    } else if (tipo === 'armazon') {
      setModalItemForm({
        marca: item.marca || '',
        modelo: item.modelo || '',
        color: item.color || '',
        material: item.material || '',
        cantidad: toStr(item.cantidad),
        medidas: item.medidas || '',
        tipo_bisagra: item.tipo_bisagra || '',
        costo: toStr(item.costo),
        precio_publico: toStr(item.precio_publico),
        precio_efectivo: toStr(item.precio_efectivo),
        id_proveedor: item.id_proveedor || '',
      });
    } else {
      setModalItemForm({
        tipo_vision: item.tipo_vision || 'Monofocal',
        marca: item.marca || '',
        color: item.color || '',
        son_esteticas: item.son_esteticas ?? false,
        rango: item.rango || '',
        precio_publico: toStr(item.precio_publico),
        precio_efectivo: toStr(item.precio_efectivo),
      });
    }
    setModalItem(true);
  };

  const handleDuplicarProducto = (p) => {
    setProductoForm({
      tipo: p.tipo,
      producto: p.producto + ' (copia)',
      precio_lista: p.precio_lista != null ? String(p.precio_lista) : '',
      precio_efectivo: p.precio_efectivo != null ? String(p.precio_efectivo) : '',
    });
    setProductoEditandoId(null);
    setModalProducto(true);
  };

  // ── modal agregar/editar item rápido ────────────────────────────────────
  const defaultCristalForm = { tipo_vision: 'Monofocal', marca: '', producto: '', laboratorio: '', material: '', tratamiento: '', tratamiento_id: null, rango: '', indice: '', stock: true, antirreflejo: false, tiene_seguro: false, tiene_promocion: false, precio_publico: '', precio_efectivo: '' };
  const defaultArmazonForm = { marca: '', modelo: '', color: '', material: '', cantidad: '', medidas: '', tipo_bisagra: '', costo: '', precio_publico: '', precio_efectivo: '', id_proveedor: '', sol: false, en_ml: false, codigo_ean: '' };
  const defaultLenteForm   = { tipo_vision: 'Monofocal', marca: '', color: '', son_esteticas: false, rango: '', precio_publico: '', precio_efectivo: '' };

  const abrirModalNuevoItem = (tipo) => {
    setModalItemTipo(tipo);
    setModalItemId(null);
    setModoNuevoCatalogoCampo({
      cristalMarca: false,
      cristalProducto: false,
      cristalMaterial: false,
      cristalLaboratorio: false,
      cristalTratamiento: false,
      armazonMarca: false,
    });
    if (tipo === 'cristal') setModalItemForm({ ...defaultCristalForm });
    else if (tipo === 'armazon') setModalItemForm({ ...defaultArmazonForm });
    else setModalItemForm({ ...defaultLenteForm });
    setModalItem(true);
  };

  const abrirModalEditarItem = (item, tipo) => {
    setModalItemTipo(tipo);
    setModalItemId(item.id);
    const toStr = v => v != null ? String(v) : '';
    setModoNuevoCatalogoCampo({
      cristalMarca: false,
      cristalProducto: false,
      cristalMaterial: false,
      cristalLaboratorio: false,
      cristalTratamiento: false,
      armazonMarca: false,
    });
    if (tipo === 'cristal') {
      setModalItemForm({
        tipo_vision: item.tipo_vision || 'Monofocal',
        marca: item.marca || '',
        producto: item.producto || '',
        laboratorio: item.laboratorio || '',
        material: item.material || '',
        tratamiento: item.tratamiento || '',
        tratamiento_id: item.tratamiento_id ?? null,
        rango: item.rango || '',
        indice: toStr(item.indice),
        stock: item.stock ?? true,
        antirreflejo: item.antirreflejo ?? false,
        tiene_seguro: item.tiene_seguro ?? false,
        tiene_promocion: item.tiene_promocion ?? false,
        precio_publico: toStr(item.precio_publico),
        precio_efectivo: toStr(item.precio_efectivo),
      });
    } else if (tipo === 'armazon') {
      setModalItemForm({
        marca: item.marca || '',
        modelo: item.modelo || '',
        color: item.color || '',
        material: item.material || '',
        cantidad: toStr(item.cantidad),
        medidas: item.medidas || '',
        tipo_bisagra: item.tipo_bisagra || '',
        costo: toStr(item.costo),
        precio_publico: toStr(item.precio_publico),
        precio_efectivo: toStr(item.precio_efectivo),
        id_proveedor: toStr(item.id_proveedor),
        sol: item.sol ?? false,
        en_ml: item.en_ml ?? false,
        codigo_ean: item.codigo_ean || '',
      });
    } else {
      setModalItemForm({
        tipo_vision: item.tipo_vision || 'Monofocal',
        marca: item.marca || '',
        color: item.color || '',
        son_esteticas: item.son_esteticas ?? false,
        rango: item.rango || '',
        precio_publico: toStr(item.precio_publico),
        precio_efectivo: toStr(item.precio_efectivo),
      });
    }
    setModalItem(true);
  };

  const handleGuardarItem = async () => {
    setGuardandoItem(true);
    try {
      const f = modalItemForm;
      if (modalItemTipo === 'cristal' && !String(f.marca || '').trim()) {
        throw new Error('La marca del cristal es obligatoria');
      }
      if (modalItemTipo === 'armazon') {
        if (!String(f.marca || '').trim()) throw new Error('La marca del armazón es obligatoria');
        if (!String(f.modelo || '').trim()) throw new Error('El modelo del armazón es obligatorio');
        if (!String(f.color || '').trim()) throw new Error('El color del armazón es obligatorio');
        if (!String(f.material || '').trim()) throw new Error('El material del armazón es obligatorio');
      }
      const numPrecio = v => v !== '' && !isNaN(parseFloat(v)) ? redondearMiles(parseFloat(v)) : null;
      const num = v => v !== '' && !isNaN(parseFloat(v)) ? parseFloat(v) : null;
      const numInt = v => v !== '' ? parseInt(v) : 0;
      let payload;
      if (modalItemTipo === 'cristal') {
        payload = {
          tipo_vision: f.tipo_vision, marca: f.marca, producto: f.producto || null,
          laboratorio: f.laboratorio || null,
          material: f.material || null,
          tratamiento: f.tratamiento || null,
          rango: f.rango || null,
          indice: num(f.indice), stock: f.stock, antirreflejo: f.antirreflejo,
          tiene_seguro: f.tiene_seguro, tiene_promocion: f.tiene_promocion,
          precio_publico: numPrecio(f.precio_publico) ?? 0,
          precio_efectivo: numPrecio(f.precio_efectivo),
          marca_id: f.marca_id || null,
          producto_id: f.producto_id || null,
          material_id: f.material_id || null,
          tratamiento_id: f.tratamiento_id || null,
          laboratorio_id: f.laboratorio_id || null,
        };
        if (modalItemId) await editarCristal(modalItemId, payload);
        else await agregarCristal(payload);
      } else if (modalItemTipo === 'armazon') {
        payload = {
          marca: f.marca, modelo: f.modelo, color: f.color, material: f.material,
          cantidad: numInt(f.cantidad),
          medidas: f.medidas || null,
          tipo_bisagra: f.tipo_bisagra || null,
          costo: numPrecio(f.costo),
          precio_publico: numPrecio(f.precio_publico) ?? 0,
          precio_efectivo: numPrecio(f.precio_efectivo),
          id_proveedor: f.id_proveedor ? parseInt(f.id_proveedor) : null,
          codigo_ean: f.codigo_ean || null,
          sol: f.sol || false,
          en_ml: f.en_ml || false,
        };
        if (modalItemId) await editarArmazon(modalItemId, payload);
        else await agregarArmazon(payload);
      } else {
        payload = {
          tipo_vision: f.tipo_vision, marca: f.marca, color: f.color || null,
          son_esteticas: f.son_esteticas, rango: f.rango || null,
          precio_publico: numPrecio(f.precio_publico) ?? 0,
          precio_efectivo: numPrecio(f.precio_efectivo),
        };
        if (modalItemId) await editarLenteContacto(modalItemId, payload);
        else await agregarLenteContacto(payload);
      }
      await cargarDatos();
      setNuevoCatalogoNombre('');
      setModalItem(false);
      Swal.fire({ icon: 'success', title: modalItemId ? 'Registro actualizado' : 'Registro agregado', toast: true, position: 'top-end', showConfirmButton: false, timer: 2000, timerProgressBar: true });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error al guardar', text: err.message });
    } finally {
      setGuardandoItem(false);
    }
  };

  // ── actualización masiva de precios ─────────────────────────────────────
  const itemsParaMasivo = useMemo(() => {
    if (masivoSeccion === 'cristales') return cristales;
    if (masivoSeccion === 'armazones') return armazones;
    if (masivoSeccion === 'lentes') return lentesContacto;
    return productos;
  }, [masivoSeccion, cristales, armazones, lentesContacto, productos]);

  const itemsFiltradosMasivo = useMemo(() => {
    if (masivoModo === 'marca' && masivoMarca) {
      return itemsParaMasivo.filter(i => normalizar(i.marca) === normalizar(masivoMarca));
    }
    return itemsParaMasivo;
  }, [itemsParaMasivo, masivoModo, masivoMarca]);

  const marcasMasivo = useMemo(() => {
    return [...new Set(itemsParaMasivo.map(i => i.marca).filter(Boolean))].sort();
  }, [itemsParaMasivo]);

  const toggleSeleccionMasivo = (id) => {
    setMasivoSeleccionados(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const seleccionarTodosMasivo = () => {
    setMasivoSeleccionados(new Set(itemsFiltradosMasivo.map(i => i.id)));
  };

  const deseleccionarTodosMasivo = () => setMasivoSeleccionados(new Set());

  const handleAplicarMasivo = async () => {
    const pct = parseFloat(masivoPorcentaje);
    if (isNaN(pct) || pct === 0) return;
    const multiplicador = 1 + pct / 100;

    const targets = masivoModo === 'seleccion'
      ? itemsParaMasivo.filter(i => masivoSeleccionados.has(i.id))
      : itemsFiltradosMasivo;

    if (targets.length === 0) return;
    const result = await Swal.fire({
      title: '¿Confirmar actualización?',
      html: `Se aplicará <strong>${pct > 0 ? '+' : ''}${pct}%</strong> a <strong>${targets.length}</strong> registro/s.<br>Esta acción modificará los precios en la base de datos.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#f59e0b',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Sí, aplicar',
      cancelButtonText: 'Cancelar',
    });
    if (!result.isConfirmed) return;

    setAplicandoMasivo(true);
    try {
      const { editarCristal: ec, editarArmazon: ea, editarLenteContacto: el, editarProducto: ep } = await import('../api/backend_v2');
      const round2 = v => redondearMiles(v);
      for (const item of targets) {
        let payload = { ...item };
        if (masivoTipoPrecio === 'lista' || masivoTipoPrecio === 'ambos') {
          const campo = masivoSeccion === 'productos' ? 'precio_lista' : 'precio_publico';
          if (item[campo]) payload[campo] = round2(parseFloat(item[campo]) * multiplicador);
        }
        if (masivoTipoPrecio === 'efectivo' || masivoTipoPrecio === 'ambos') {
          if (item.precio_efectivo != null) payload.precio_efectivo = round2(parseFloat(item.precio_efectivo) * multiplicador);
        }
        if (masivoSeccion === 'cristales') await ec(item.id, payload);
        else if (masivoSeccion === 'armazones') await ea(item.id, payload);
        else if (masivoSeccion === 'lentes') await el(item.id, payload);
        else await ep(item.id, payload);
      }
      await cargarDatos();
      setModalMasivo(false);
      setMasivoSeleccionados(new Set());
      setMasivoPorcentaje('');
      Swal.fire({ icon: 'success', title: `${targets.length} registro/s actualizados`, toast: true, position: 'top-end', showConfirmButton: false, timer: 3000, timerProgressBar: true });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error en actualización masiva', text: err.message });
    } finally {
      setAplicandoMasivo(false);
    }
  };

  // ── exportar / imprimir ──────────────────────────────────────────────────
  const handleAbrirModalImprimir = () => {
    const esCristales = filtroCategoria === 'todos' || filtroCategoria === 'cristales';
    const esLentes    = filtroCategoria === 'todos' || filtroCategoria === 'lentes-contacto';
    const esArmazones = filtroCategoria === 'todos' || filtroCategoria === 'armazones';
    const esProductos = filtroCategoria === 'todos' || filtroCategoria === 'productos';
    setImprimirOpciones({
      monofocal:   esCristales && (!filtroTipoCristal || filtroTipoCristal === 'Monofocal'),
      bifocal:     esCristales && (!filtroTipoCristal || filtroTipoCristal === 'Bifocal'),
      multifocal:  esCristales && (!filtroTipoCristal || filtroTipoCristal === 'Multifocal'),
      ocupacional: esCristales && (!filtroTipoCristal || filtroTipoCristal === 'Ocupacional'),
      lentes:      esLentes,
      armazones:   esArmazones,
      productos:   esProductos,
    });
    setModalImprimir(true);
  };

  const handleImprimir = (opciones) => {
    const fecha = new Date().toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });

    const getRowBg = (marca, index, fallbackEven, fallbackOdd) => {
      const colores = marcaColores[normalizar(marca)];
      if (colores) {
        const cls = index % 2 === 0 ? colores.even : colores.odd;
        return TAILWIND_HEX[cls] || (index % 2 === 0 ? fallbackEven : fallbackOdd);
      }
      return index % 2 === 0 ? fallbackEven : fallbackOdd;
    };

    const buildTable = (headers, rows) =>
      `<table><thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>${rows}</tbody></table>`;

    let seccionesHTML = '';

    if (filtroCategoria === 'todos' || filtroCategoria === 'cristales') {
      [
        { titulo: 'Cristales Monofocales',   lista: cristalesMonofocales,   tipo: 'Monofocal',   key: 'monofocal',   accent: '#3b82f6', even: '#eff6ff', odd: '#dbeafe' },
        { titulo: 'Cristales Bifocales',      lista: cristalesBifocales,     tipo: 'Bifocal',     key: 'bifocal',     accent: '#a855f7', even: '#faf5ff', odd: '#f3e8ff' },
        { titulo: 'Cristales Multifocales',   lista: cristalesMultifocales,  tipo: 'Multifocal',  key: 'multifocal',  accent: '#10b981', even: '#ecfdf5', odd: '#d1fae5' },
        { titulo: 'Cristales Ocupacionales',  lista: cristalesOcupacionales, tipo: 'Ocupacional', key: 'ocupacional', accent: '#06b6d4', even: '#ecfeff', odd: '#cffafe' },
      ]
        .filter(g => (!filtroTipoCristal || filtroTipoCristal === g.tipo) && opciones[g.key])
        .forEach(({ titulo, lista, accent, even, odd }) => {
          if (lista.length === 0) return;
          seccionesHTML += `<div class="seccion"><h2 style="border-left-color:${accent}">${titulo} (${lista.length})</h2>${buildTable(
            ['Marca', 'Producto', 'Material', 'Rango', 'Índice', 'AR', 'Stock', 'Precio Lista', 'Precio Efectivo'],
            lista.map((c, i) => `<tr style="background:${getRowBg(c.marca, i, even, odd)}"><td>${c.marca || '-'}</td><td>${c.producto || '-'}</td><td>${c.material || '-'}</td><td>${c.rango || '-'}</td><td>${c.indice || '-'}</td><td>${c.tratamiento || (c.antirreflejo ? 'Antirreflejo' : 'Sin tratamiento')}</td><td>${c.stock ? 'Stock' : 'Lab.'}</td><td class="precio">${formatearPrecio(c.precio_publico)}</td><td class="precio">${c.precio_efectivo != null ? formatearPrecio(c.precio_efectivo) : '-'}</td></tr>`).join('')
          )}</div>`;
        });
    }

    if (opciones.lentes && (filtroCategoria === 'todos' || filtroCategoria === 'lentes-contacto')) {
      if (lentesContactoFiltrados.length > 0) {
        seccionesHTML += `<div class="seccion"><h2 style="border-left-color:#14b8a6">Lentes de Contacto (${lentesContactoFiltrados.length})</h2>${buildTable(
          ['Marca', 'Rango', 'Color', 'Estética', 'Precio Lista', 'Precio Efectivo'],
          lentesContactoFiltrados.map((l, i) => `<tr style="background:${getRowBg(l.marca, i, '#f0fdfa', '#ccfbf1')}"><td>${l.marca || '-'}</td><td>${l.rango || '-'}</td><td>${l.color || '-'}</td><td>${l.son_esteticas ? 'Sí' : 'No'}</td><td class="precio">${formatearPrecio(l.precio_publico)}</td><td class="precio">${l.precio_efectivo != null ? formatearPrecio(l.precio_efectivo) : '-'}</td></tr>`).join('')
        )}</div>`;
      }
    }

    if (opciones.armazones && (filtroCategoria === 'todos' || filtroCategoria === 'armazones')) {
      if (armazonesFiltrados.length > 0) {
        seccionesHTML += `<div class="seccion"><h2 style="border-left-color:#f97316">Armazones (${armazonesFiltrados.length})</h2>${buildTable(
          ['Modelo', 'Marca', 'Material', 'Stock', 'Precio Lista', 'Precio Efectivo'],
          armazonesFiltrados.map((a, i) => `<tr style="background:${getRowBg(a.marca, i, '#fff7ed', '#ffedd5')}"><td>${a.modelo || '-'}</td><td>${a.marca || '-'}</td><td>${a.material || '-'}</td><td>${a.cantidad > 0 ? `${a.cantidad} u.` : 'Sin stock'}</td><td class="precio">${formatearPrecio(a.precio_publico)}</td><td class="precio">${a.precio_efectivo != null ? formatearPrecio(a.precio_efectivo) : '-'}</td></tr>`).join('')
        )}</div>`;
      }
    }

    if (opciones.productos && (filtroCategoria === 'todos' || filtroCategoria === 'productos')) {
      if (productosFiltrados.length > 0) {
        seccionesHTML += `<div class="seccion"><h2 style="border-left-color:#6366f1">Productos / Fornituras (${productosFiltrados.length})</h2>${buildTable(
          ['Tipo', 'Producto', 'Precio Lista', 'Precio Efectivo'],
          productosFiltrados.map((p, i) => `<tr style="background:${i % 2 === 0 ? '#f9fafb' : '#eef2ff'}"><td>${p.tipo || '-'}</td><td>${p.producto || '-'}</td><td class="precio">${formatearPrecio(p.precio_lista)}</td><td class="precio">${p.precio_efectivo != null ? formatearPrecio(p.precio_efectivo) : '-'}</td></tr>`).join('')
        )}</div>`;
      }
    }

    const metaFiltros = [
      filtroMarca       ? `Marca: ${filtroMarca}`         : '',
      filtroTipoCristal ? `Tipo: ${filtroTipoCristal}`    : '',
      busquedaGlobal    ? `Búsqueda: "${busquedaGlobal}"` : '',
    ].filter(Boolean).join(' · ');

    const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8" />
  <title>Lista de Precios — ${fecha}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 11px; color: #111; padding: 20px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    header { margin-bottom: 16px; border-bottom: 2px solid #374151; padding-bottom: 10px; display: flex; justify-content: space-between; align-items: flex-end; }
    header h1 { font-size: 20px; font-weight: 700; color: #111827; }
    header p { font-size: 10px; color: #6b7280; margin-top: 3px; }
    header .fecha { font-size: 11px; color: #6b7280; text-align: right; }
    .seccion { margin-bottom: 18px; }
    .seccion h2 { font-size: 12px; font-weight: 700; background: #f3f4f6; padding: 5px 8px; border-left: 4px solid #3b82f6; margin-bottom: 4px; color: #1f2937; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    table { width: 100%; border-collapse: collapse; font-size: 10px; }
    thead tr { background: #e5e7eb !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    th { padding: 5px 8px; text-align: left; font-weight: 600; color: #374151; border-bottom: 1px solid #d1d5db; white-space: nowrap; }
    td { padding: 4px 8px; border-bottom: 1px solid #f3f4f6; color: #374151; }
    tr { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .precio { font-weight: 700; color: #065f46; white-space: nowrap; }
    @media print {
      body { padding: 8px; }
      .seccion { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <header>
    <div>
      <h1>Lista de Precios</h1>
      <p>Descuento efectivo: ${descuentoEfectivo}%${metaFiltros ? ' · ' + metaFiltros : ''}</p>
    </div>
    <div class="fecha">${fecha}</div>
  </header>
  ${seccionesHTML || '<p style="color:#6b7280;margin-top:20px">Sin resultados para los filtros actuales.</p>'}
</body>
</html>`;

    const win = window.open('', '_blank', 'width=950,height=750');
    if (!win) {
      Swal.fire({ icon: 'warning', title: 'Ventana bloqueada', text: 'El navegador bloqueó la ventana emergente. Permitila e intentá nuevamente.' });
      return;
    }
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400);
  };




  // ── Th ordenable ────────────────────────────────────────────────────────
  const Th = ({ label, col, sortState, onSort, className = '' }) => (
    <th
      className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none ${className}`}
      onClick={() => onSort(col)}
    >
      {label}
      <SortIcon col={col} sortCol={sortState.col} sortDir={sortState.dir} />
    </th>
  );

  const catalogoTipos = [
    { key: 'laboratorios', label: 'Laboratorios' },
    { key: 'productos_cristales', label: 'Productos cristales' },
    { key: 'materiales_cristales', label: 'Materiales cristales' },
    { key: 'tratamientos', label: 'Tratamientos' },
    { key: 'marcas_cristales', label: 'Marcas cristales' },
    { key: 'marcas_armazones', label: 'Marcas armazones' },
  ];

  const itemsCatalogoActivo = useMemo(() => {
    return (catalogos[catalogoActivo] || [])
      .filter(item => {
        const nombre = String(item.nombre || '').toLowerCase();
        const busqueda = catalogoBusqueda.trim().toLowerCase();
        const matchTexto = !busqueda || nombre.includes(busqueda);
        const matchEstado =
          catalogoEstadoFiltro === 'todos'
            ? true
            : catalogoEstadoFiltro === 'activos'
              ? item.activo
              : !item.activo;
        return matchTexto && matchEstado;
      })
      .slice()
      .sort((a, b) => String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es'));
  }, [catalogos, catalogoActivo, catalogoBusqueda, catalogoEstadoFiltro]);

  const opcionesCatalogoActivas = useMemo(() => {
    const onlyActive = (arr) => (arr || []).filter(i => i.activo).map(i => i.nombre).filter(Boolean);
    return {
      laboratorios: onlyActive(catalogos.laboratorios),
      marcasCristales: onlyActive(catalogos.marcas_cristales),
      productosCristales: onlyActive(catalogos.productos_cristales),
      materialesCristales: onlyActive(catalogos.materiales_cristales),
      tratamientos: onlyActive(catalogos.tratamientos),
      marcasArmazones: onlyActive(catalogos.marcas_armazones),
      materialesArmazones: onlyActive(catalogos.material_armazon),
      coloresArmazones: onlyActive(catalogos.color_armazon),
    };
  }, [catalogos]);

  const valorEnCatalogo = (lista, valor) => {
    const target = normalizar(valor);
    if (!target) return true;
    return (lista || []).some(item => normalizar(item) === target);
  };

  const optionLegado = (listaActiva, valor) => {
    const limpio = String(valor ?? '').trim();
    if (!limpio) return null;
    if (valorEnCatalogo(listaActiva, limpio)) return null;
    return <option key={`legado-${limpio}`} value={limpio}>{`Valor legado: ${limpio}`}</option>;
  };

  const refrescarCatalogos = async () => {
    const resCristales = await obtenerCatalogosCristales(false);
    const resArmazones = await obtenerCatalogosArmazones(false);
    setCatalogos({
      laboratorios: resCristales.laboratorios || [],
      marcas_cristales: resCristales.marcas_cristales || [],
      productos_cristales: resCristales.productos_cristales || [],
      materiales_cristales: resCristales.materiales_cristales || [],
      tratamientos: resCristales.tratamientos || [],
      marcas_armazones: resCristales.marcas_armazones || [],
      material_armazon: resArmazones.material_armazon || [],
      color_armazon: resArmazones.color_armazon || [],
    });
  };

  const handleCrearCatalogo = async () => {
    const nombre = nuevoCatalogoNombre.trim();
    if (!nombre) return;
    setGuardandoCatalogo(true);
    try {
      const payload = { nombre };
      if (catalogoActivo === 'laboratorios') {
        payload.direccion = nuevoLaboratorioDireccion.trim() || null;
        payload.telefonos = nuevoLaboratorioTelefonos
          .split(/[;,\n]/)
          .map(t => t.trim())
          .filter(Boolean);
      } else if (catalogoActivo === 'materiales_cristales') {
        payload.rango = nuevoMaterialRango.trim() || null;
      }
      await crearCatalogoCristalesItem(catalogoActivo, payload.nombre, payload.direccion, payload.telefonos, payload.rango);
      await refrescarCatalogos();
      setNuevoCatalogoNombre('');
      setNuevoLaboratorioDireccion('');
      setNuevoLaboratorioTelefonos('');
      setNuevoMaterialRango('');
      Swal.fire({ icon: 'success', title: 'Ítem guardado', toast: true, position: 'top-end', showConfirmButton: false, timer: 1800, timerProgressBar: true });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error al guardar catálogo', text: err.message });
    } finally {
      setGuardandoCatalogo(false);
    }
  };

  const handleRenombrarCatalogo = async (item) => {
    const { value: nuevoNombre } = await Swal.fire({
      title: 'Renombrar ítem',
      input: 'text',
      inputValue: item.nombre,
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      inputValidator: (v) => (!String(v || '').trim() ? 'Ingresá un nombre' : null),
    });
    if (!nuevoNombre) return;
    try {
      await actualizarCatalogoCristalesItem(catalogoActivo, item.id, { nombre: nuevoNombre.trim() });
      await refrescarCatalogos();
      Swal.fire({ icon: 'success', title: 'Ítem actualizado', toast: true, position: 'top-end', showConfirmButton: false, timer: 1800, timerProgressBar: true });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo renombrar', text: err.message });
    }
  };

  const handleEditarLaboratorioDatos = async (item) => {
    const telefonosIniciales = Array.isArray(item.telefonos) ? item.telefonos.join(', ') : '';
    const result = await Swal.fire({
      title: 'Editar laboratorio',
      html: `
        <input id="swal-lab-nombre" class="swal2-input" placeholder="Nombre" value="${String(item.nombre || '').replace(/"/g, '&quot;')}">
        <input id="swal-lab-direccion" class="swal2-input" placeholder="Dirección" value="${String(item.direccion || '').replace(/"/g, '&quot;')}">
        <input id="swal-lab-telefonos" class="swal2-input" placeholder="Teléfonos (separados por coma)" value="${String(telefonosIniciales).replace(/"/g, '&quot;')}">
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        const nombre = document.getElementById('swal-lab-nombre')?.value?.trim() || '';
        const direccion = document.getElementById('swal-lab-direccion')?.value?.trim() || '';
        const telefonosRaw = document.getElementById('swal-lab-telefonos')?.value || '';
        if (!nombre) {
          Swal.showValidationMessage('El nombre es obligatorio');
          return null;
        }
        return {
          nombre,
          direccion,
          telefonos: telefonosRaw.split(/[;,\n]/).map(t => t.trim()).filter(Boolean),
        };
      },
    });

    if (!result.isConfirmed || !result.value) return;
    try {
      await actualizarCatalogoCristalesItem(catalogoActivo, item.id, result.value);
      await refrescarCatalogos();
      Swal.fire({ icon: 'success', title: 'Laboratorio actualizado', toast: true, position: 'top-end', showConfirmButton: false, timer: 1800, timerProgressBar: true });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo actualizar', text: err.message });
    }
  };

  const handleEditarMaterialDatos = async (item) => {
    const result = await Swal.fire({
      title: 'Editar material',
      html: `
        <input id="swal-mat-nombre" class="swal2-input" placeholder="Nombre" value="${String(item.nombre || '').replace(/"/g, '&quot;')}">
        <input id="swal-mat-rango" class="swal2-input" placeholder="Rango" value="${String(item.rango || '').replace(/"/g, '&quot;')}">
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Guardar',
      cancelButtonText: 'Cancelar',
      preConfirm: () => {
        const nombre = document.getElementById('swal-mat-nombre')?.value?.trim() || '';
        const rango = document.getElementById('swal-mat-rango')?.value?.trim() || '';
        if (!nombre) {
          Swal.showValidationMessage('El nombre es obligatorio');
          return null;
        }
        return { nombre, rango };
      },
    });

    if (!result.isConfirmed || !result.value) return;
    try {
      await actualizarCatalogoCristalesItem(catalogoActivo, item.id, result.value);
      await refrescarCatalogos();
      Swal.fire({ icon: 'success', title: 'Material actualizado', toast: true, position: 'top-end', showConfirmButton: false, timer: 1800, timerProgressBar: true });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo actualizar', text: err.message });
    }
  };

  const handleToggleActivoCatalogo = async (item) => {
    try {
      if (item.activo) {
        await desactivarCatalogoCristalesItem(catalogoActivo, item.id);
      } else {
        await actualizarCatalogoCristalesItem(catalogoActivo, item.id, { activo: true });
      }
      await refrescarCatalogos();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo actualizar estado', text: err.message });
    }
  };

  const handleEliminarCatalogoItem = async (item) => {
    const result = await Swal.fire({
      title: 'Eliminar ítem',
      text: `Se eliminará definitivamente "${item.nombre}" del catálogo. Esta acción no se puede deshacer.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#dc2626',
    });

    if (!result.isConfirmed) return;

    try {
      await eliminarCatalogoCristalesItem(catalogoActivo, item.id);
      await refrescarCatalogos();
      Swal.fire({ icon: 'success', title: 'Ítem eliminado', toast: true, position: 'top-end', showConfirmButton: false, timer: 1800, timerProgressBar: true });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudo eliminar', text: err.message });
    }
  };

  const handleDetectarDuplicados = async () => {
    setCargandoDuplicados(true);
    try {
      const res = await detectarDuplicadosCatalogoCristales(catalogoActivo);
      setDuplicadosCatalogo(res.grupos || []);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'No se pudieron detectar duplicados', text: err.message });
    } finally {
      setCargandoDuplicados(false);
    }
  };

  // ── tabla cristales ──────────────────────────────────────────────────────
  const renderTablaCristales = (lista, estilo, mostrarTipo = false, pageKey = '') => {
    const page = pageKey ? (cristalPages[pageKey] ?? 0) : 0;
    const totalPages = pageKey ? Math.ceil(lista.length / pageSize) : 1;
    const paginada = pageKey ? lista.slice(page * pageSize, (page + 1) * pageSize) : lista;

    if (lista.length === 0) return (
      <div className="px-6 py-4 text-sm text-gray-500">No hay productos en este grupo</div>
    );
    return (
      <div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={estilo.theadClass}>
              <tr>
                {mostrarTipo && (
                  <Th label="Tipo" col="tipo_vision" sortState={sortCristales} onSort={col => toggleSort(setSortCristales, col)} className={estilo.textClass} />
                )}
                <Th label="Marca" col="marca" sortState={sortCristales} onSort={col => toggleSort(setSortCristales, col)} className={estilo.textClass} />
                <Th label="Producto" col="producto" sortState={sortCristales} onSort={col => toggleSort(setSortCristales, col)} className={estilo.textClass} />
                <Th label="Material" col="material" sortState={sortCristales} onSort={col => toggleSort(setSortCristales, col)} className={estilo.textClass} />
                <Th label="Tratamiento" col="tratamiento" sortState={sortCristales} onSort={col => toggleSort(setSortCristales, col)} className={estilo.textClass} />
                <Th label="Precio Lista" col="precio_publico" sortState={sortCristales} onSort={col => toggleSort(setSortCristales, col)} className={estilo.textClass} />
                <Th label="Precio Efectivo" col="precio_efectivo" sortState={sortCristales} onSort={col => toggleSort(setSortCristales, col)} className={estilo.textClass} />
                <Th label="Rango" col="rango" sortState={sortCristales} onSort={col => toggleSort(setSortCristales, col)} className={estilo.textClass} />
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${estilo.textClass}`}>Características</th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${estilo.textClass}`}>Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginada.map((cristal, index) => (
                <tr key={cristal.id} className={getRowClassMarca(marcaColores, cristal.marca, index, 'bg-white', estilo.altRowClass)}>
                  {mostrarTipo && (
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        cristal.tipo_vision === 'Multifocal' ? 'bg-emerald-100 text-emerald-800'
                          : cristal.tipo_vision === 'Ocupacional' ? 'bg-teal-100 text-teal-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>{cristal.tipo_vision}</span>
                    </td>
                  )}
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    <span>{cristal.marca}</span>
                    {!valorEnCatalogo(opcionesCatalogoActivas.marcasCristales, cristal.marca) && cristal.marca && (
                      <span className="ml-2 px-2 py-0.5 text-[10px] rounded-full bg-amber-100 text-amber-700 align-middle">legado</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-800">
                    <span>{cristal.producto || '-'}</span>
                    {cristal.producto && !valorEnCatalogo(opcionesCatalogoActivas.productosCristales, cristal.producto) && (
                      <span className="ml-2 px-2 py-0.5 text-[10px] rounded-full bg-amber-100 text-amber-700 align-middle">legado</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    <span>{cristal.material || '-'}</span>
                    {cristal.material && !valorEnCatalogo(opcionesCatalogoActivas.materialesCristales, cristal.material) && (
                      <span className="ml-2 px-2 py-0.5 text-[10px] rounded-full bg-amber-100 text-amber-700 align-middle">legado</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${cristal.tratamiento ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
                      {cristal.tratamiento || (cristal.antirreflejo ? 'Antirreflejo' : 'Sin tratamiento')}
                    </span>
                  </td>
                  <td className={`px-6 py-4 text-sm font-bold ${estilo.priceTextClass}`}>{formatearPrecio(cristal.precio_publico)}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{cristal.precio_efectivo != null ? formatearPrecio(cristal.precio_efectivo) : '-'}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 max-w-[140px]">
                    {cristal.rango
                      ? <span title={cristal.rango} className="block truncate cursor-help">{cristal.rango}</span>
                      : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex space-x-1">
                      {cristal.tiene_seguro && <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">Seguro</span>}
                      {cristal.tiene_promocion && <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Promocion</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex gap-1">
                      <button
                        onClick={() => abrirModalEditarItem(cristal, 'cristal')}
                        className={`px-2 py-1 text-white text-xs rounded focus:ring-2 flex items-center gap-1 ${estilo.buttonClass}`}
                        title="Editar datos"
                      >
                        <PencilIcon className="h-3 w-3" /><span className="hidden sm:inline">Editar</span>
                      </button>
                      <button
                        onClick={() => handleEditarPrecio(cristal, 'cristal')}
                        className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 flex items-center gap-1"
                        title="Editar precio"
                      >
                        <CurrencyDollarIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDuplicarItem(cristal, 'cristal')}
                        className="px-2 py-1 bg-gray-400 text-white text-xs rounded hover:bg-gray-500 flex items-center gap-1"
                        title="Duplicar registro"
                      >
                        <DocumentDuplicateIcon className="h-3 w-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pageKey && <Paginador page={page} totalPages={totalPages} total={lista.length} pageSize={pageSize} setPage={p => setCristalPage(pageKey, p)} />}
      </div>
    );
  };

  const renderBloqueCristales = (titulo, icono, lista, estilo, emptyText, mostrarTipo = false, keyPrefix = '', separarSecciones = true) => {
    const stockItems = lista.filter(i => i.stock);
    const labItems   = lista.filter(i => !i.stock);
    return (
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        <div className={`${estilo.headerClass} px-5 sm:px-6 py-3.5 sm:py-4`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center flex-wrap gap-2.5 sm:gap-3">
              {icono && React.createElement(icono, { className: "h-6 w-6 text-white shrink-0" })}
              <h3 className="text-lg font-bold text-white leading-none">{titulo}</h3>
              <span className="bg-white/20 backdrop-blur-xs px-2.5 py-0.5 rounded-full text-xs font-semibold text-white">
                {lista.length} {lista.length === 1 ? 'producto' : 'productos'}
              </span>
            </div>
            <button
              type="button"
              onClick={() => abrirModalNuevoItem('cristal')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/20 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-white/30 border border-white/30 transition-colors shadow-2xs cursor-pointer"
            >
              <PlusIcon className="h-4 w-4" />
              <span>Agregar</span>
            </button>
          </div>
        </div>
        {lista.length > 0 ? (
          separarSecciones ? (
            <div className="divide-y divide-gray-200">
              <div>
                <div className="px-6 py-3 bg-green-50 border-b border-green-100 flex items-center justify-between">
                  <span className="text-sm font-semibold text-green-700">Stock</span>
                  <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">{stockItems.length}</span>
                </div>
                {stockItems.length > 0
                  ? renderTablaCristales(stockItems, estilo, mostrarTipo, keyPrefix ? `${keyPrefix}-stock` : '')
                  : <p className="px-6 py-4 text-sm text-gray-400 italic">Sin resultados en stock.</p>
                }
              </div>
              <div>
                <div className="px-6 py-3 bg-red-50 border-b border-red-100 flex items-center justify-between">
                  <span className="text-sm font-semibold text-red-700">Laboratorio</span>
                  <span className="text-xs font-medium text-red-700 bg-red-100 px-2 py-1 rounded-full">{labItems.length}</span>
                </div>
                {labItems.length > 0
                  ? renderTablaCristales(labItems, estilo, mostrarTipo, keyPrefix ? `${keyPrefix}-lab` : '')
                  : <p className="px-6 py-4 text-sm text-gray-400 italic">Sin resultados en laboratorio.</p>
                }
              </div>
            </div>
          ) : (
            renderTablaCristales(lista, estilo, mostrarTipo, keyPrefix ? `${keyPrefix}-all` : '')
          )
        ) : (
          <div className="px-6 py-8 text-center text-gray-500">{emptyText}</div>
        )}
      </div>
    );
  };

  // ── loading / error ─────────────────────────────────────────────────────
  if (loading) return (
    <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Cargando lista de precios...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-red-800 mb-2">Error al cargar datos</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={cargarDatos} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">Reintentar</button>
        </div>
      </div>
    </div>
  );

  // ── render ──────────────────────────────────────────────────────────────
  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center space-x-3">
                <BanknotesIcon className="h-7 w-7 sm:h-8 sm:w-8 text-green-500" />
                <span>Lista de Precios</span>
              </h1>
              <p className="text-gray-600 mt-1 text-sm sm:text-base">Consulta y administra precios de todos los productos</p>
            </div>
            <div className="flex flex-col items-stretch lg:items-end gap-3">
              <div className="flex flex-wrap items-center lg:justify-end gap-2">
                <button
                  onClick={() => { setMasivoSeccion('cristales'); setMasivoMarca(''); setMasivoSeleccionados(new Set()); setMasivoPorcentaje(''); setMasivoModo('marca'); setMasivoTipoPrecio('lista'); setModalMasivo(true); }}
                  className="flex items-center gap-2 px-3 py-2 bg-amber-500 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-amber-600"
                >
                  <ChartBarIcon className="h-4 w-4 flex-shrink-0" /><span>Actualizar precios masivo</span>
                </button>
                <button
                  onClick={handleAbrirModalImprimir}
                  className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-green-700"
                >
                  <PrinterIcon className="h-4 w-4 flex-shrink-0" /><span>Imprimir / Exportar</span>
                </button>
                <button
                  onClick={() => setModalColores(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-indigo-500 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-indigo-600"
                >
                  <SwatchIcon className="h-4 w-4 flex-shrink-0" /><span>Colores de marcas</span>
                </button>
                <button
                  onClick={() => {
                    setCatalogoBusqueda('');
                    setCatalogoEstadoFiltro('todos');
                    setNuevoCatalogoNombre('');
                    setNuevoLaboratorioDireccion('');
                    setNuevoLaboratorioTelefonos('');
                    setNuevoMaterialRango('');
                    setDuplicadosCatalogo([]);
                    setModalCatalogos(true);
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-blue-700"
                >
                  <BeakerIcon className="h-4 w-4 flex-shrink-0" /><span>Herramientas cristales</span>
                </button>
              </div>
              <div className="text-left lg:text-right text-sm">
                <div className="text-gray-500">Última actualización</div>
                <div className="text-lg font-semibold text-gray-900 leading-tight">{new Date().toLocaleDateString('es-AR')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Configuración comercial: descuento efectivo y cuotas sin interés */}
        <div className="bg-green-50 border border-green-200 rounded-xl px-5 py-3 mb-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Descuento Cristales / General */}
            <div className="flex items-center gap-2">
              <CurrencyDollarIcon className="h-5 w-5 text-green-700 shrink-0" />
              <span className="text-sm font-semibold text-green-800">Desc. cristales:</span>
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  value={descuentoEfectivo}
                  onChange={e => {
                    const v = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                    setDescuentoEfectivo(v);
                    localStorage.setItem(_descuentoKey, String(v));
                    // Guardar en BD con debounce de 1s
                    if (_descuentoDebounce.current) clearTimeout(_descuentoDebounce.current);
                    _descuentoDebounce.current = setTimeout(() => {
                      guardarDescuentoEfectivo(v).catch(() => {});
                    }, 1000);
                  }}
                  className="w-16 px-2 py-1 text-sm border border-green-300 rounded-lg focus:ring-2 focus:ring-green-400 text-center font-bold bg-white"
                  min="0" max="100" step="1"
                />
                <span className="text-sm font-bold text-green-700">%</span>
              </div>
            </div>

            <div className="hidden sm:block h-6 w-px bg-green-200" />

            {/* Descuento Armazones */}
            <div className="flex items-center gap-2">
              <TagIcon className="h-5 w-5 text-green-700 shrink-0" />
              <span className="text-sm font-semibold text-green-800">Desc. armazones:</span>
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  value={descuentoArmazones}
                  onChange={e => {
                    const v = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                    setDescuentoArmazones(v);
                    localStorage.setItem(_descuentoArmazonesKey, String(v));
                    // Guardar en BD con debounce de 1s
                    if (_descuentoArmazonesDebounce.current) clearTimeout(_descuentoArmazonesDebounce.current);
                    _descuentoArmazonesDebounce.current = setTimeout(() => {
                      guardarDescuentoArmazones(v).catch(() => {});
                    }, 1000);
                  }}
                  className="w-16 px-2 py-1 text-sm border border-green-300 rounded-lg focus:ring-2 focus:ring-green-400 text-center font-bold bg-white"
                  min="0" max="100" step="1"
                />
                <span className="text-sm font-bold text-green-700">%</span>
              </div>
            </div>

            <div className="hidden md:block h-6 w-px bg-green-200" />

            {/* Cuotas sin interés */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1.5">
                <CreditCardIcon className="h-5 w-5 text-green-700 shrink-0" />
                <span className="text-sm font-semibold text-green-800">Cuotas sin interés:</span>
              </div>
              <div className="flex items-center gap-1.5">
                {OPCIONES_CUOTAS_SISTEMA.map((cuota) => {
                  const activa = cuotasDisponibles.includes(cuota);
                  return (
                    <button
                      key={cuota}
                      type="button"
                      onClick={() => toggleCuotaOptica(cuota)}
                      className={`px-2.5 py-1 text-xs font-bold rounded-lg border transition-all cursor-pointer ${
                        activa
                          ? 'bg-green-700 text-white border-green-700 shadow-2xs'
                          : 'bg-white text-green-800 border-green-300 hover:bg-green-100/50'
                      }`}
                    >
                      {cuota} cuotas
                    </button>
                  );
                })}
              </div>
              {cuotasDisponibles.length > 0 && (
                <span className="text-xs font-bold text-green-900 bg-green-200/60 px-2.5 py-0.5 rounded-full ml-1">
                  {formatearTextoCuotas(cuotasDisponibles)}
                </span>
              )}
            </div>

            <div className="hidden lg:block h-6 w-px bg-green-200" />

            {/* Botón Políticas Comerciales y Descuentos 
            <button
              type="button"
              onClick={() => setModalPoliticaOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-xl shadow-sm transition-all hover:scale-105 active:scale-95 border border-slate-700 cursor-pointer"
              title="Configurar descuentos por tipo (Receta/Sol) y reglas por marca (Cacic, etc.)"
            >
              <SparklesIcon className="w-4 h-4 text-cyan-400" />
              <span>Políticas por Marca y Tipo</span>
            </button> */}

            {/* Botón Mercado Pago 
            <button
              type="button"
              onClick={() => setModalMPOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-sky-700 hover:bg-sky-600 rounded-xl shadow-sm transition-all hover:scale-105 active:scale-95 border border-sky-500 cursor-pointer"
              title="Configurar credenciales de Mercado Pago y Webhook para cobros online"
            >
              <CreditCardIcon className="w-4 h-4 text-sky-200" />
              <span>Mercado Pago</span>
            </button>*/}
          </div>

          <span className="text-xs text-green-700 hidden xl:inline">
            Se sincroniza automáticamente con presupuestos y lista de precios.
          </span>
        </div>


        {/* Estadísticas — tipos de cristales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          {[
                      { label: 'Monofocales',   value: estadisticas.monofocales,   icon: SunIcon,          color: 'from-blue-400 to-blue-500',    tipo: 'Monofocal'   },
            { label: 'Multifocales',  value: estadisticas.multifocales,  icon: Squares2X2Icon,   color: 'from-violet-500 to-violet-600', tipo: 'Multifocal'  },
            { label: 'Bifocales',     value: estadisticas.bifocales,     icon: ViewColumnsIcon,  color: 'from-sky-500 to-sky-600',       tipo: 'Bifocal'     },
            { label: 'Ocupacionales', value: estadisticas.ocupacionales, icon: BriefcaseIcon,    color: 'from-cyan-500 to-cyan-600',     tipo: 'Ocupacional' },
          ].map(({ label, value, icon: Icon, color, tipo }) => {
            const activo = filtroCategoria === 'cristales' && filtroTipoCristal === tipo;
            return (
              <button
                key={tipo}
                onClick={() => {
                  if (activo) { setFiltroCategoria('todos'); setFiltroTipoCristal(''); }
                  else { setFiltroCategoria('cristales'); setFiltroTipoCristal(tipo); }
                }}
                className={`bg-gradient-to-r ${color} rounded-lg shadow text-white p-3 text-left w-full transition-all
                  hover:scale-105 hover:shadow-lg active:scale-100
                  ${activo ? 'ring-4 ring-white ring-opacity-70 scale-105 shadow-lg' : 'opacity-85 hover:opacity-100'}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-xs font-medium">{label}</p>
                    <p className="text-xl font-bold">{value}</p>
                    {activo && <p className="text-xs text-white/60 mt-0.5">activo ↓</p>}
                  </div>
                  <Icon className="h-7 w-7 text-white/80" />
                </div>
              </button>
            );
          })}
        </div>

       

        {/* Estadísticas — secciones */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          {[
                        { label: 'Lentes Contacto',       value: estadisticas.totalLentesContacto, icon: EyeDropperIcon,   color: 'from-teal-500 to-teal-600',    filtro: 'lentes-contacto' },
            { label: 'Armazones',              value: estadisticas.totalArmazones,       icon: RectangleGroupIcon, color: 'from-green-500 to-green-600',  filtro: 'armazones'       },
            { label: 'Productos / Fornituras', value: estadisticas.totalProductos,       icon: ArchiveBoxIcon,   color: 'from-indigo-500 to-indigo-600', filtro: 'productos'       },
          ].map(({ label, value, icon: Icon, color, filtro }) => {
            const activo = filtroCategoria === filtro;
            return (
              <button
                key={filtro}
                onClick={() => {
                  setFiltroTipoCristal('');
                  setFiltroCategoria(prev => prev === filtro ? 'todos' : filtro);
                }}
                className={`bg-gradient-to-r ${color} rounded-lg shadow-lg text-white p-4 text-left w-full transition-all
                  hover:scale-105 hover:shadow-xl active:scale-100
                  ${activo ? 'ring-4 ring-white ring-opacity-70 scale-105 shadow-xl' : 'opacity-90 hover:opacity-100'}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/80 text-sm font-medium">{label}</p>
                    <p className="text-2xl font-bold">{value}</p>
                    {activo && <p className="text-xs text-white/60 mt-1">Mostrando sólo esta sección ↓</p>}
                  </div>
                  <Icon className="h-8 w-8 text-white/80" />
                </div>
              </button>
            );
          })}
        </div>

         {(filtroCategoria === 'todos' || filtroCategoria === 'cristales') && laboratoriosResumen.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-700">Laboratorios</p>
              {filtroLaboratorio && (
                <button
                  onClick={() => setFiltroLaboratorio('')}
                  className="text-xs text-red-500 hover:text-red-700 font-medium"
                >
                  Limpiar laboratorio
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {laboratoriosResumen.map(({ laboratorio, cantidad }) => {
                const activo = normalizar(filtroLaboratorio) === normalizar(laboratorio);
                return (
                  <button
                    key={laboratorio}
                    onClick={() => {
                      setFiltroCategoria('cristales');
                      setFiltroLaboratorio(prev => normalizar(prev) === normalizar(laboratorio) ? '' : laboratorio);
                    }}
                    className={`px-3 py-1.5 rounded-full border text-sm transition-colors ${
                      activo
                        ? 'bg-cyan-100 text-cyan-800 border-cyan-300 font-semibold'
                        : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-cyan-50 hover:border-cyan-200'
                    }`}
                  >
                    {laboratorio} ({cantidad})
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6 space-y-3">
          {/* categoría + búsqueda */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              {[
                              { key: 'todos',           label: 'Todos',      icon: ListBulletIcon    },
                { key: 'cristales',     label: 'Cristales',  icon: BeakerIcon        },
                { key: 'armazones',     label: 'Armazones',  icon: RectangleGroupIcon },
                { key: 'lentes-contacto', label: 'Lentes C.', icon: EyeDropperIcon   },
                { key: 'productos',     label: 'Productos',  icon: ArchiveBoxIcon    },
              ].map(({ key, label, icon: Icon }) => (
                <button key={key} onClick={() => setFiltroCategoria(key)}
                  className={`flex items-center space-x-1 px-3 py-2 rounded-lg font-medium text-sm transition-all ${
                    filtroCategoria === key
                      ? 'bg-blue-100 text-blue-700 border-2 border-blue-200'
                      : 'text-gray-600 hover:bg-gray-100 border-2 border-transparent'
                  }`}
                >
                  <Icon className="h-4 w-4" /><span>{label}</span>
                </button>
              ))}
            </div>
            <div className="relative w-full lg:w-96">
              <input type="text" placeholder="Buscar (ej: producto:varilux material:blue)..."
                value={busquedaGlobal} onChange={e => setBusquedaGlobal(e.target.value)}
                className="w-full px-4 py-2 pl-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              {busquedaGlobal && (
                <button onClick={() => setBusquedaGlobal('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">✖</button>
              )}
            </div>
          </div>

          {/* marca / tipo cristal */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Marca:</label>
              <select value={filtroMarca} onChange={e => setFiltroMarca(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-300"
              >
                <option value="">Todas</option>
                {marcasUnicas.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Tipo cristal:</label>
              <select value={filtroTipoCristal} onChange={e => setFiltroTipoCristal(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-300"
              >
                <option value="">Todos</option>
                {tiposCristalUnicos.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Laboratorio:</label>
              <select value={filtroLaboratorio} onChange={e => setFiltroLaboratorio(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-300"
              >
                <option value="">Todos</option>
                {laboratoriosUnicos.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            {(filtroMarca || filtroTipoCristal || filtroLaboratorio || busquedaGlobal) && (
              <button onClick={() => { setFiltroMarca(''); setFiltroTipoCristal(''); setFiltroLaboratorio(''); setBusquedaGlobal(''); }}
                className="text-xs text-red-500 hover:text-red-700 font-medium"
              >
                <ArrowPathIcon className="h-3 w-3 inline mr-1" />Limpiar filtros
              </button>
            )}
            <div className="flex items-center gap-2 ml-auto">
              <label className="text-sm font-medium text-gray-700">Filas:</label>
              <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
                className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-300"
              >
                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* ── Cristales ──────────────────────────────────────────────── */}
        {(filtroCategoria === 'todos' || filtroCategoria === 'cristales') && (
          <div className="space-y-6">
            {(!filtroTipoCristal || filtroTipoCristal === 'Monofocal') && (!hayBusquedaGlobal || cristalesMonofocales.length > 0) && renderBloqueCristales(
              'Cristales Monofocales', SunIcon, cristalesMonofocales,
              { headerClass: 'bg-gradient-to-r from-blue-500 to-blue-600', theadClass: 'bg-blue-50', textClass: 'text-blue-600', priceTextClass: 'text-blue-600', altRowClass: 'bg-blue-50', buttonClass: 'bg-blue-500 hover:bg-blue-600 focus:ring-blue-500' },
              'No hay cristales monofocales registrados', false, 'mono'
            )}
            {(!filtroTipoCristal || filtroTipoCristal === 'Bifocal') && (!hayBusquedaGlobal || cristalesBifocales.length > 0) && renderBloqueCristales(
              'Cristales Bifocales', ViewColumnsIcon, cristalesBifocales,
              { headerClass: 'bg-gradient-to-r from-purple-500 to-purple-600', theadClass: 'bg-purple-50', textClass: 'text-purple-600', priceTextClass: 'text-purple-600', altRowClass: 'bg-purple-50', buttonClass: 'bg-purple-500 hover:bg-purple-600 focus:ring-purple-500' },
              'No hay cristales bifocales registrados', false, 'bi'
            )}
            {(!filtroTipoCristal || filtroTipoCristal === 'Multifocal') && (!hayBusquedaGlobal || cristalesMultifocales.length > 0) && renderBloqueCristales(
              'Cristales Multifocales', Squares2X2Icon, cristalesMultifocales,
              { headerClass: 'bg-gradient-to-r from-emerald-500 to-emerald-600', theadClass: 'bg-emerald-50', textClass: 'text-emerald-600', priceTextClass: 'text-emerald-600', altRowClass: 'bg-emerald-50', buttonClass: 'bg-emerald-500 hover:bg-emerald-600 focus:ring-emerald-500' },
              'No hay cristales multifocales registrados', false, 'multi', false
            )}
            {(!filtroTipoCristal || filtroTipoCristal === 'Ocupacional') && (!hayBusquedaGlobal || cristalesOcupacionales.length > 0) && renderBloqueCristales(
              'Cristales Ocupacionales', BriefcaseIcon, cristalesOcupacionales,
              { headerClass: 'bg-gradient-to-r from-cyan-500 to-cyan-600', theadClass: 'bg-cyan-50', textClass: 'text-cyan-600', priceTextClass: 'text-cyan-600', altRowClass: 'bg-cyan-50', buttonClass: 'bg-cyan-500 hover:bg-cyan-600 focus:ring-cyan-500' },
              'No hay cristales ocupacionales registrados', false, 'ocup'
            )}
          </div>
        )}

        {/* ── Lentes de Contacto ──────────────────────────────────────── */}
        {(filtroCategoria === 'todos' || filtroCategoria === 'lentes-contacto') && (
          <div className="mt-6">
            {(!hayBusquedaGlobal || lentesContactoFiltrados.length > 0) && (
              <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-5 sm:px-6 py-3.5 sm:py-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center flex-wrap gap-2.5 sm:gap-3">
                      <EyeDropperIcon className="h-6 w-6 text-white shrink-0" />
                      <h3 className="text-lg font-bold text-white leading-none">Lentes de Contacto</h3>
                      <span className="bg-white/20 backdrop-blur-xs px-2.5 py-0.5 rounded-full text-xs font-semibold text-white">
                        {lentesContactoFiltrados.length} {lentesContactoFiltrados.length === 1 ? 'producto' : 'productos'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => abrirModalNuevoItem('lente')}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/20 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-white/30 border border-white/30 transition-colors shadow-2xs cursor-pointer"
                    >
                      <PlusIcon className="h-4 w-4" />
                      <span>Agregar</span>
                    </button>
                  </div>
                </div>
                {lentesContactoFiltrados.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-teal-50">
                        <tr>
                          <Th label="Marca" col="marca" sortState={sortLentes} onSort={col => toggleSort(setSortLentes, col)} className="text-teal-600" />
                          <Th label="Rango" col="rango" sortState={sortLentes} onSort={col => toggleSort(setSortLentes, col)} className="text-teal-600" />
                          <Th label="Color" col="color" sortState={sortLentes} onSort={col => toggleSort(setSortLentes, col)} className="text-teal-600" />
                          <Th label="Precio Lista" col="precio_publico" sortState={sortLentes} onSort={col => toggleSort(setSortLentes, col)} className="text-teal-600" />
                          <Th label="Precio Efectivo" col="precio_efectivo" sortState={sortLentes} onSort={col => toggleSort(setSortLentes, col)} className="text-teal-600" />
                          <th className="px-6 py-3 text-left text-xs font-medium text-teal-600 uppercase tracking-wider">Estética</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-teal-600 uppercase tracking-wider">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {lentesContactoFiltrados.map((lente, index) => (
                          <tr key={lente.id} className={index % 2 === 0 ? 'bg-white' : 'bg-teal-50'}>
                            <td className="px-6 py-4 text-sm font-medium text-gray-900">{lente.marca}</td>
                            <td className="px-6 py-4 text-sm text-gray-600 max-w-[140px]">
                              {lente.rango
                                ? <span title={lente.rango} className="block truncate cursor-help">{lente.rango}</span>
                                : '-'}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-700">{lente.color || '-'}</td>
                            <td className="px-6 py-4 text-sm font-bold text-teal-600">{formatearPrecio(lente.precio_publico)}</td>
                            <td className="px-6 py-4 text-sm text-gray-700">{lente.precio_efectivo != null ? formatearPrecio(lente.precio_efectivo) : '-'}</td>
                            <td className="px-6 py-4 text-sm">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${lente.son_esteticas ? 'bg-pink-100 text-pink-800' : 'bg-gray-100 text-gray-800'}`}>
                                {lente.son_esteticas ? 'Sí' : 'No'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-sm">
                              <div className="flex gap-1">
                                <button onClick={() => abrirModalEditarItem(lente, 'lente')}
                                  className="px-2 py-1 bg-teal-500 text-white text-xs rounded hover:bg-teal-600 flex items-center gap-1"
                                >
                                  <PencilIcon className="h-3 w-3" /><span className="hidden sm:inline">Editar</span>
                                </button>
                                <button onClick={() => handleEditarPrecio(lente, 'lente_contacto')}
                                  className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 flex items-center gap-1"
                                  title="Editar precio"
                                ><CurrencyDollarIcon className="h-5 w-5" /></button>
                                <button onClick={() => handleDuplicarItem(lente, 'lente')}
                                  className="px-2 py-1 bg-gray-400 text-white text-xs rounded hover:bg-gray-500 flex items-center gap-1"
                                  title="Duplicar registro"
                                ><DocumentDuplicateIcon className="h-3 w-3" /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="px-6 py-8 text-center text-gray-500">No hay lentes de contacto registrados</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Armazones ───────────────────────────────────────────────── */}
        {(filtroCategoria === 'todos' || filtroCategoria === 'armazones') && (
          <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden mt-6">
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-5 sm:px-6 py-4">
              <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
                {/* Título y badge */}
                <div className="flex items-center flex-wrap gap-2.5 sm:gap-3">
                  <EyeIcon className="h-7 w-7 text-white shrink-0" />
                  <h3 className="text-lg sm:text-xl font-bold text-white leading-none">Armazones</h3>
                  <span className="bg-white/20 backdrop-blur-xs px-2.5 py-0.5 rounded-full text-xs font-semibold text-white">
                    {armazonesFiltrados.length} de {armazones.length}
                  </span>
                </div>

                {/* Acciones y buscador alineados */}
                <div className="flex flex-wrap sm:flex-nowrap items-center gap-2.5">
                  <button
                    type="button"
                    onClick={() => abrirModalNuevoItem('armazon')}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-white/20 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-white/30 border border-white/30 transition-colors shadow-2xs cursor-pointer whitespace-nowrap"
                  >
                    <PlusIcon className="h-4 w-4" />
                    <span>Agregar</span>
                  </button>

                  <div className="relative flex-1 sm:w-72">
                    <input
                      type="text"
                      placeholder="Buscar armazón..."
                      value={busquedaArmazones}
                      onChange={e => setBusquedaArmazones(e.target.value)}
                      className="w-full px-3.5 py-2 pl-9 text-xs sm:text-sm bg-white text-gray-800 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-white/40 shadow-xs placeholder-gray-400"
                    />
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    {busquedaArmazones && (
                      <button
                        type="button"
                        onClick={() => setBusquedaArmazones('')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs p-1"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <select
                    value={tamanoPaginaArmazones}
                    onChange={e => setTamanoPaginaArmazones(parseInt(e.target.value, 10))}
                    className="px-2.5 py-2 text-xs sm:text-sm bg-white text-gray-700 border border-white/40 rounded-xl focus:outline-none focus:ring-2 focus:ring-white/40 shadow-xs cursor-pointer"
                  >
                    <option value={8}>8 / pág</option>
                    <option value={12}>12 / pág</option>
                    <option value={20}>20 / pág</option>
                    <option value={40}>40 / pág</option>
                  </select>
                </div>
              </div>
            </div>
            {armazonesFiltrados.length > 0 ? (
              <>
              <div className="overflow-x-auto">
                <table className="min-w-[980px] w-full">
                  <thead className="bg-orange-50">
                    <tr>
                      <Th label="Marca" col="marca" sortState={sortArmazones} onSort={col => toggleSort(setSortArmazones, col)} className="text-orange-600" />
                      <Th label="Modelo" col="modelo" sortState={sortArmazones} onSort={col => toggleSort(setSortArmazones, col)} className="text-orange-600" />
                      <Th label="Color" col="color" sortState={sortArmazones} onSort={col => toggleSort(setSortArmazones, col)} className="text-orange-600" />
                      <Th label="Precio Lista" col="precio_publico" sortState={sortArmazones} onSort={col => toggleSort(setSortArmazones, col)} className="text-orange-600" />
                      <Th label="Precio Efectivo" col="precio_efectivo" sortState={sortArmazones} onSort={col => toggleSort(setSortArmazones, col)} className="text-orange-600" />
                      <Th label="Stock" col="cantidad" sortState={sortArmazones} onSort={col => toggleSort(setSortArmazones, col)} className="text-orange-600" />
                      <Th label="Material" col="material" sortState={sortArmazones} onSort={col => toggleSort(setSortArmazones, col)} className="text-orange-600" />
                      <th className="px-6 py-3 text-left text-xs font-medium text-orange-600 uppercase tracking-wider">Tipo</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-orange-600 uppercase tracking-wider">ML</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-orange-600 uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {armazonesPaginados.map((armazon, index) => (
                      <tr key={armazon.id} className={index % 2 === 0 ? 'bg-white' : 'bg-orange-50'}>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <span>{armazon.marca}</span>
                          {armazon.marca && !valorEnCatalogo(opcionesCatalogoActivas.marcasArmazones, armazon.marca) && (
                            <span className="ml-2 px-2 py-0.5 text-[10px] rounded-full bg-amber-100 text-amber-800 align-middle">legado</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{armazon.modelo}</td>
                        <td className="px-6 py-4 text-sm">
                          <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">{armazon.color || 'N/A'}</span>
                        </td>
                        <td className="px-6 py-4 text-sm font-bold text-orange-600">{formatearPrecio(armazon.precio_publico)}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">{armazon.precio_efectivo != null ? formatearPrecio(armazon.precio_efectivo) : '-'}</td>
                        <td
                          className="px-6 py-4 text-sm"
                          onMouseEnter={() => setHoverStockArmazonId(armazon.id)}
                          onMouseLeave={() => setHoverStockArmazonId(null)}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${armazon.cantidad > 0 ? 'bg-green-300 text-green-800' : 'bg-red-100 text-red-800'}`}>
                              {armazon.cantidad > 0 ? `${armazon.cantidad} u.` : 'Sin stock'}
                            </span>
                            <button
                              type="button"
                              onClick={() => setTouchStockArmazonId((prev) => (prev === armazon.id ? null : armazon.id))}
                              className="inline-flex sm:hidden px-1.5 py-0.5 text-[11px] rounded border border-gray-300 text-gray-700"
                              title="Ajustar stock"
                            >
                              ±
                            </button>
                            {(hoverStockArmazonId === armazon.id || touchStockArmazonId === armazon.id) && (
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleAjustarStockArmazonRapido(armazon, -1)}
                                  disabled={actualizandoStockArmazonId === armazon.id || (parseInt(armazon.cantidad, 10) || 0) <= 0}
                                  className="px-1.5 py-0.5 text-xs rounded border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-40"
                                  title="Restar 1"
                                >
                                  -1
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleAjustarStockArmazonRapido(armazon, +1)}
                                  disabled={actualizandoStockArmazonId === armazon.id}
                                  className="px-1.5 py-0.5 text-xs rounded border border-green-200 text-green-700 hover:bg-green-50 disabled:opacity-40"
                                  title="Sumar 1"
                                >
                                  +1
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">{armazon.material || 'N/A'}</span>
                        </td>

                        <td className="px-6 py-4 text-sm">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${armazon.sol ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>
                            {armazon.sol ? 'Sol' : 'Receta'}
                          </span>
                        </td>

                        <td className="px-6 py-4 text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            armazon.en_ml 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'text-gray-400'
                          }`}>
                            {armazon.en_ml ? 'Publicado' : '-'}
                          </span>
                        </td>

                        <td className="px-6 py-4 text-sm">
                          <div className="flex gap-1">
                            <button onClick={() => abrirModalEditarItem(armazon, 'armazon')}
                              className="px-2 py-1 bg-orange-500 text-white text-xs rounded hover:bg-orange-600 flex items-center gap-1"
                            >
                              <PencilIcon className="h-3 w-3" /><span className="hidden sm:inline">Editar</span>
                            </button>
                            <button onClick={() => handleEditarPrecio(armazon, 'armazon')}
                              className="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 flex items-center gap-1"
                              title="Editar precio"
                            ><CurrencyDollarIcon className="h-5 w-5" /></button>
                            <button onClick={() => handleDuplicarItem(armazon, 'armazon')}
                              className="px-2 py-1 bg-gray-400 text-white text-xs rounded hover:bg-gray-500 flex items-center gap-1"
                              title="Duplicar registro"
                            ><DocumentDuplicateIcon className="h-3 w-3" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Paginador
                page={paginaArmazones}
                totalPages={totalPaginasArmazones}
                total={armazonesFiltrados.length}
                pageSize={tamanoPaginaArmazones}
                setPage={setPaginaArmazones}
              />
              </>
            ) : (
              <div className="px-6 py-8 text-center text-gray-500">
                {busquedaArmazones
                  ? <div><p className="mb-2">No se encontraron armazones para "{busquedaArmazones}"</p>
                  <button onClick={() => setBusquedaArmazones('')} className="text-orange-600 hover:text-orange-700 text-sm font-medium flex items-center gap-1"><ArrowPathIcon className="h-3 w-3" /> Limpiar</button></div>
                  : 'No hay armazones registrados'}
              </div>
            )}
          </div>
        )}

        {/* ── Productos ───────────────────────────────────────────────── */}
        {(filtroCategoria === 'todos' || filtroCategoria === 'productos') && (
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden mt-6">
            <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-5 sm:px-6 py-3.5 sm:py-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center flex-wrap gap-2.5 sm:gap-3">
                  <ArchiveBoxIcon className="h-6 w-6 text-white shrink-0" />
                  <h3 className="text-lg font-bold text-white leading-none">Productos</h3>
                  <span className="bg-white/20 backdrop-blur-xs px-2.5 py-0.5 rounded-full text-xs font-semibold text-white">
                    {productosFiltrados.length} de {productos.length}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={abrirModalNuevoProducto}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-indigo-700 text-xs sm:text-sm font-semibold rounded-xl hover:bg-indigo-50 transition-colors shadow-2xs cursor-pointer"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>Agregar</span>
                </button>
              </div>
            </div>
            {productosFiltrados.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-indigo-50">
                    <tr>
                      <Th label="Tipo" col="tipo" sortState={sortProductos} onSort={col => toggleSort(setSortProductos, col)} className="text-indigo-600" />
                      <Th label="Producto" col="producto" sortState={sortProductos} onSort={col => toggleSort(setSortProductos, col)} className="text-indigo-600" />
                      <Th label="Precio Lista" col="precio_lista" sortState={sortProductos} onSort={col => toggleSort(setSortProductos, col)} className="text-indigo-600" />
                      <Th label="Precio Efectivo" col="precio_efectivo" sortState={sortProductos} onSort={col => toggleSort(setSortProductos, col)} className="text-indigo-600" />
                      <th className="px-6 py-3 text-left text-xs font-medium text-indigo-600 uppercase tracking-wider">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {productosFiltrados.map((p, index) => (
                      <tr key={p.id} className={index % 2 === 0 ? 'bg-white' : 'bg-indigo-50'}>
                        <td className="px-6 py-4 text-sm">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800">{p.tipo}</span>
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{p.producto}</td>
                        <td className="px-6 py-4 text-sm font-bold text-indigo-600">{formatearPrecio(p.precio_lista)}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">{p.precio_efectivo != null ? formatearPrecio(p.precio_efectivo) : '-'}</td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex gap-2">
                            <button onClick={() => abrirModalEditarProducto(p)}
                              className="px-3 py-1 bg-indigo-500 text-white text-xs rounded hover:bg-indigo-600 flex items-center gap-1"
                            >
                              <PencilIcon className="h-3 w-3" /><span className="hidden sm:inline">Editar</span>
                            </button>
                            <button onClick={() => handleDuplicarProducto(p)}
                              className="px-3 py-1 bg-gray-400 text-white text-xs rounded hover:bg-gray-500 flex items-center gap-1"
                              title="Duplicar registro"
                            >
                              <DocumentDuplicateIcon className="h-3 w-3" />
                            </button>
                            <button onClick={() => handleEliminarProducto(p.id)}
                              className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 flex items-center gap-1"
                            >
                              <TrashIcon className="h-3 w-3" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="px-6 py-8 text-center text-gray-500">
                No hay productos registrados.{' '}
                <button onClick={abrirModalNuevoProducto} className="text-indigo-600 hover:text-indigo-700 font-medium">Agregar el primero</button>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 bg-gray-100 rounded-lg p-4">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div><span className="font-medium">Nota:</span> Los precios pueden variar según promociones vigentes</div>
            <div className="flex items-center space-x-4">
              <span className="flex items-center gap-2">
                <ChartBarIcon className="h-5 w-5" />
                Total: {estadisticas.totalCristales + estadisticas.totalArmazones + estadisticas.totalLentesContacto + estadisticas.totalProductos}
              </span>
              <button onClick={cargarDatos} className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700 flex items-center gap-1">
                <ArrowPathIcon className="h-3 w-3" /> Actualizar
              </button>
            </div>
          </div>
        </div>

        {/* ── Modal edición de precio ──────────────────────────────────── */}
        {editando && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={e => { if (e.target === e.currentTarget) cerrarModalEdicion(); }}
          >
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-green-900 flex items-center space-x-2">
                  <CurrencyDollarIcon className="h-5 w-5 text-green-500" /><span>Editar Precios</span>
                </h3>
                <button onClick={cerrarModalEdicion} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-600 mb-1">
                  {tipoItemEditando === 'cristal' ? 'Cristal' : tipoItemEditando === 'lente_contacto' ? 'Lente de Contacto' : 'Armazón'}:{' '}
                  <span className="font-medium text-gray-900">{itemEditando?.marca}{itemEditando?.modelo && ` - ${itemEditando.modelo}`}</span>
                </p>
                <p className="text-xs text-gray-500">Precio lista actual: {formatearPrecio(itemEditando?.precio_publico)}</p>
              </div>
              <div className="space-y-3 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Precio Lista</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input type="number" value={precioEdicion} onChange={e => {
                      const v = e.target.value;
                      setPrecioEdicion(v);
                      if (v !== '' && !isNaN(parseFloat(v)) && parseFloat(v) > 0) {
                        setPrecioEfectivoEdicion(String(redondearMiles(parseFloat(v) * (1 - descuentoEfectivo / 100))));
                      }
                    }}
                      className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0" min="0" step="1" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Precio Efectivo <span className="text-gray-400 font-normal">({descuentoEfectivo}% desc.)</span></label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input type="number" value={precioEfectivoEdicion} onChange={e => setPrecioEfectivoEdicion(e.target.value)}
                      className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="0" min="0" step="1" />
                  </div>
                </div>
              </div>
              <div className="flex space-x-3">
                <button onClick={cerrarModalEdicion} className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancelar</button>
                <button onClick={handleGuardarPrecio} className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700">Guardar</button>
              </div>
            </div>
          </div>
        )}

        {/* ── Modal producto ───────────────────────────────────────────── */}
        {modalProducto && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={e => { if (e.target === e.currentTarget) setModalProducto(false); }}
          >
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                  <ArchiveBoxIcon className="h-5 w-5 text-gray-500" /><span>{productoEditandoId ? 'Editar Producto' : 'Nuevo Producto'}</span>
                </h3>
                <button onClick={() => setModalProducto(false)} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
              </div>
              <div className="space-y-3 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo / Sección <span className="text-red-500">*</span></label>
                  <input type="text" value={productoForm.tipo} onChange={e => setProductoForm(f => ({ ...f, tipo: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="ej: Accesorios, Servicios, Limpieza..." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Producto <span className="text-red-500">*</span></label>
                  <input type="text" value={productoForm.producto} onChange={e => setProductoForm(f => ({ ...f, producto: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    placeholder="Nombre del producto" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Precio Lista</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input type="number" value={productoForm.precio_lista} onChange={e => {
                      const v = e.target.value;
                      setProductoForm(f => ({
                        ...f,
                        precio_lista: v,
                        precio_efectivo: v !== '' && !isNaN(parseFloat(v)) && parseFloat(v) > 0
                          ? String(redondearMiles(parseFloat(v) * (1 - descuentoEfectivo / 100)))
                          : f.precio_efectivo,
                      }));
                    }}
                      className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="0" min="0" step="1" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Precio Efectivo <span className="text-gray-400 font-normal">(opcional)</span></label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input type="number" value={productoForm.precio_efectivo} onChange={e => setProductoForm(f => ({ ...f, precio_efectivo: e.target.value }))}
                      className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      placeholder="0" min="0" step="0.01" />
                  </div>
                </div>
              </div>
              <div className="flex space-x-3">
                <button onClick={() => setModalProducto(false)} className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancelar</button>
                <button
                  onClick={handleGuardarProducto}
                  disabled={guardandoProducto || !productoForm.tipo.trim() || !productoForm.producto.trim()}
                  className="flex-1 px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {guardandoProducto ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Modal agregar/editar item rápido ────────────────────────── */}
        {modalItem && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={e => { if (e.target === e.currentTarget) setModalItem(false); }}
          >
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {modalItemId ? 'Editar' : 'Agregar'} {modalItemTipo === 'cristal' ? 'Cristal' : modalItemTipo === 'armazon' ? 'Armazón' : 'Lente de Contacto'}
                </h3>
                <button onClick={() => setModalItem(false)} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
              </div>

              {/* Cristal form */}
              {modalItemTipo === 'cristal' && (
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Tipo visión <span className="text-red-500">*</span></label>
                    <select value={modalItemForm.tipo_vision} onChange={e => setModalItemForm(f => ({ ...f, tipo_vision: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                      {['Monofocal','Bifocal','Multifocal','Ocupacional'].map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Marca <span className="text-red-500">*</span></label>
                    {!modoNuevoCatalogoCampo.cristalMarca ? (
                      <div className="space-y-1">
                        <select
                          value={modalItemForm.marca || ''}
                          onChange={e => {
                            if (e.target.value === '__nuevo__') {
                              setModoNuevoCatalogoCampo(prev => ({ ...prev, cristalMarca: true }));
                              setModalItemForm(f => ({ ...f, marca: '' }));
                            } else {
                              setModalItemForm(f => ({ ...f, marca: e.target.value }));
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Seleccionar marca...</option>
                          {optionLegado(opcionesCatalogoActivas.marcasCristales, modalItemForm.marca)}
                          {opcionesCatalogoActivas.marcasCristales.map(nombre => <option key={`mcs-${nombre}`} value={nombre}>{nombre}</option>)}
                          <option value="__nuevo__">+ Crear nueva marca...</option>
                        </select>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <input type="text" value={modalItemForm.marca} onChange={e => setModalItemForm(f => ({ ...f, marca: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="Nueva marca" />
                        <button type="button" onClick={() => setModoNuevoCatalogoCampo(prev => ({ ...prev, cristalMarca: false }))} className="text-xs text-blue-600 hover:underline">
                          Usar catálogo existente
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Producto</label>
                    {!modoNuevoCatalogoCampo.cristalProducto ? (
                      <select
                        value={modalItemForm.producto || ''}
                        onChange={e => {
                          if (e.target.value === '__nuevo__') {
                            setModoNuevoCatalogoCampo(prev => ({ ...prev, cristalProducto: true }));
                            setModalItemForm(f => ({ ...f, producto: '' }));
                          } else {
                            setModalItemForm(f => ({ ...f, producto: e.target.value }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Seleccionar producto...</option>
                        {optionLegado(opcionesCatalogoActivas.productosCristales, modalItemForm.producto)}
                        {opcionesCatalogoActivas.productosCristales.map(nombre => <option key={`pcs-${nombre}`} value={nombre}>{nombre}</option>)}
                        <option value="__nuevo__">+ Crear nuevo producto...</option>
                      </select>
                    ) : (
                      <div className="space-y-1">
                        <input type="text" value={modalItemForm.producto} onChange={e => setModalItemForm(f => ({ ...f, producto: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="Nuevo producto" />
                        <button type="button" onClick={() => setModoNuevoCatalogoCampo(prev => ({ ...prev, cristalProducto: false }))} className="text-xs text-blue-600 hover:underline">
                          Usar catálogo existente
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Laboratorio</label>
                    {!modoNuevoCatalogoCampo.cristalLaboratorio ? (
                      <select
                        value={modalItemForm.laboratorio || ''}
                        onChange={e => {
                          if (e.target.value === '__nuevo__') {
                            setModoNuevoCatalogoCampo(prev => ({ ...prev, cristalLaboratorio: true }));
                            setModalItemForm(f => ({ ...f, laboratorio: '' }));
                          } else {
                            setModalItemForm(f => ({ ...f, laboratorio: e.target.value }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Seleccionar laboratorio...</option>
                        {optionLegado(opcionesCatalogoActivas.laboratorios, modalItemForm.laboratorio)}
                        {opcionesCatalogoActivas.laboratorios.map(nombre => <option key={`lcs-${nombre}`} value={nombre}>{nombre}</option>)}
                        <option value="__nuevo__">+ Crear nuevo laboratorio...</option>
                      </select>
                    ) : (
                      <div className="space-y-1">
                        <input type="text" value={modalItemForm.laboratorio} onChange={e => setModalItemForm(f => ({ ...f, laboratorio: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="Nuevo laboratorio" />
                        <button type="button" onClick={() => setModoNuevoCatalogoCampo(prev => ({ ...prev, cristalLaboratorio: false }))} className="text-xs text-blue-600 hover:underline">
                          Usar catálogo existente
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Material</label>
                    {!modoNuevoCatalogoCampo.cristalMaterial ? (
                      <select
                        value={modalItemForm.material || ''}
                        onChange={e => {
                          if (e.target.value === '__nuevo__') {
                            setModoNuevoCatalogoCampo(prev => ({ ...prev, cristalMaterial: true }));
                            setModalItemForm(f => ({ ...f, material: '' }));
                          } else {
                            setModalItemForm(f => ({ ...f, material: e.target.value }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Seleccionar material...</option>
                        {optionLegado(opcionesCatalogoActivas.materialesCristales, modalItemForm.material)}
                        {opcionesCatalogoActivas.materialesCristales.map(nombre => <option key={`mtcs-${nombre}`} value={nombre}>{nombre}</option>)}
                        <option value="__nuevo__">+ Crear nuevo material...</option>
                      </select>
                    ) : (
                      <div className="space-y-1">
                        <input type="text" value={modalItemForm.material} onChange={e => setModalItemForm(f => ({ ...f, material: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="Nuevo material" />
                        <button type="button" onClick={() => setModoNuevoCatalogoCampo(prev => ({ ...prev, cristalMaterial: false }))} className="text-xs text-blue-600 hover:underline">
                          Usar catálogo existente
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Rango</label>
                    <input type="text" value={modalItemForm.rango} onChange={e => setModalItemForm(f => ({ ...f, rango: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="Ej: 0 a -6" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Índice</label>
                    <input type="number" value={modalItemForm.indice} onChange={e => setModalItemForm(f => ({ ...f, indice: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="1.5" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Precio Lista</label>
                    <input type="number" value={modalItemForm.precio_publico} onChange={e => {
                      const v = e.target.value;
                      setModalItemForm(f => ({
                        ...f,
                        precio_publico: v,
                        precio_efectivo: v !== '' && !isNaN(parseFloat(v)) && parseFloat(v) > 0
                          ? String(redondearMiles(parseFloat(v) * (1 - descuentoEfectivo / 100)))
                          : f.precio_efectivo,
                      }));
                    }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="0" min="0" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Precio Efectivo <span className="text-gray-400 font-normal">({descuentoEfectivo}% desc.)</span></label>
                    <input type="number" value={modalItemForm.precio_efectivo} onChange={e => setModalItemForm(f => ({ ...f, precio_efectivo: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="opcional" min="0" />
                  </div>
                  <div className="col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={modalItemForm.stock ?? false} onChange={e => setModalItemForm(f => ({ ...f, stock: e.target.checked }))}
                        className="rounded border-gray-300" />
                      Stock
                    </label>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Tratamiento</label>
                      {!modoNuevoCatalogoCampo.cristalTratamiento ? (
                        <select
                          value={modalItemForm.tratamiento || ''}
                          onChange={e => {
                            if (e.target.value === '__nuevo__') {
                              setModoNuevoCatalogoCampo(prev => ({ ...prev, cristalTratamiento: true }));
                              setModalItemForm(f => ({ ...f, tratamiento: '', tratamiento_id: null }));
                            } else {
                              const nombre = e.target.value;
                              const found = (catalogos.tratamientos || []).find(i => i.nombre === nombre);
                              setModalItemForm(f => ({ ...f, tratamiento: nombre, tratamiento_id: found ? found.id : null }));
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Sin tratamiento</option>
                          {optionLegado(opcionesCatalogoActivas.tratamientos, modalItemForm.tratamiento)}
                          {opcionesCatalogoActivas.tratamientos.map(nombre => <option key={`trt-mot-${nombre}`} value={nombre}>{nombre}</option>)}
                          <option value="__nuevo__">+ Crear nuevo tratamiento...</option>
                        </select>
                      ) : (
                        <div className="space-y-1">
                          <input
                            type="text"
                            value={modalItemForm.tratamiento || ''}
                            onChange={e => setModalItemForm(f => ({ ...f, tratamiento: e.target.value, tratamiento_id: null }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                            placeholder="Nuevo tratamiento"
                          />
                          <button type="button" onClick={() => setModoNuevoCatalogoCampo(prev => ({ ...prev, cristalTratamiento: false }))} className="text-xs text-blue-600 hover:underline">
                            Usar catálogo existente
                          </button>
                        </div>
                      )}
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={modalItemForm.tiene_seguro ?? false} onChange={e => setModalItemForm(f => ({ ...f, tiene_seguro: e.target.checked }))}
                        className="rounded border-gray-300" />
                      Seguro
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={modalItemForm.tiene_promocion ?? false} onChange={e => setModalItemForm(f => ({ ...f, tiene_promocion: e.target.checked }))}
                        className="rounded border-gray-300" />
                      Promoción
                    </label>
                  </div>
                </div>
              )}

              {/* Armazón form */}
              {modalItemTipo === 'armazon' && (
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Marca <span className="text-red-500">*</span></label>
                    {!modoNuevoCatalogoCampo.armazonMarca ? (
                      <select
                        value={modalItemForm.marca || ''}
                        onChange={e => {
                          if (e.target.value === '__nuevo__') {
                            setModoNuevoCatalogoCampo(prev => ({ ...prev, armazonMarca: true }));
                            setModalItemForm(f => ({ ...f, marca: '' }));
                          } else {
                            // Auto-asignar proveedor si la marca ya tiene uno
                            const idProveedor = obtenerProveedorPorMarca(e.target.value);
                            setModalItemForm(f => ({ 
                              ...f, 
                              marca: e.target.value,
                              ...(idProveedor ? { id_proveedor: idProveedor.toString() } : {})
                            }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="">Seleccionar marca...</option>
                        {optionLegado(opcionesCatalogoActivas.marcasArmazones, modalItemForm.marca)}
                        {opcionesCatalogoActivas.marcasArmazones.map(nombre => <option key={`mas-${nombre}`} value={nombre}>{nombre}</option>)}
                        <option value="__nuevo__">+ Crear nueva marca...</option>
                      </select>
                    ) : (
                      <div className="space-y-1">
                        <input type="text" value={modalItemForm.marca} onChange={e => setModalItemForm(f => ({ ...f, marca: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500" placeholder="Nueva marca" />
                        <button type="button" onClick={() => setModoNuevoCatalogoCampo(prev => ({ ...prev, armazonMarca: false }))} className="text-xs text-orange-600 hover:underline">
                          Usar catálogo existente
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Modelo <span className="text-red-500">*</span></label>
                    <input type="text" value={modalItemForm.modelo} onChange={e => setModalItemForm(f => ({ ...f, modelo: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500" placeholder="Ej: mod. 123" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Color <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={modalItemForm.color || ''}
                      onChange={e => setModalItemForm(f => ({ ...f, color: e.target.value }))}
                      placeholder="Escribir o seleccionar..."
                      list="coloresArmazonesListModal"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
                    />
                    <datalist id="coloresArmazonesListModal">
                      {opcionesCatalogoActivas.coloresArmazones.map(nombre => (
                        <option key={`col-modal-${nombre}`} value={nombre} />
                      ))}
                    </datalist>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Material <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={modalItemForm.material || ''}
                      onChange={e => setModalItemForm(f => ({ ...f, material: e.target.value }))}
                      placeholder="Escribir o seleccionar..."
                      list="materialesArmazonesListModal"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500"
                    />
                    <datalist id="materialesArmazonesListModal">
                      {opcionesCatalogoActivas.materialesArmazones.map(nombre => (
                        <option key={`mat-modal-${nombre}`} value={nombre} />
                      ))}
                    </datalist>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Cantidad</label>
                    <input type="number" value={modalItemForm.cantidad} onChange={e => setModalItemForm(f => ({ ...f, cantidad: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500" min="0" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Medidas</label>
                    <input type="text" value={modalItemForm.medidas || ''} onChange={e => setModalItemForm(f => ({ ...f, medidas: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500" placeholder="Ej: 54-18-145" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Tipo Bisagra</label>
                    <select value={modalItemForm.tipo_bisagra || ''} onChange={e => setModalItemForm(f => ({ ...f, tipo_bisagra: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500">
                      <option value="">Sin especificar</option>
                      <option value="Clasica">Clásica</option>
                      <option value="Flex">Flex</option>
                      <option value="Triple">Triple</option>
                      <option value="Silicona">Silicona</option>
                      <option value="Otros">Otros</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Costo</label>
                    <input type="number" value={modalItemForm.costo || ''} onChange={e => setModalItemForm(f => ({ ...f, costo: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500" min="0" step="0.01" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Proveedor</label>
                    <select value={modalItemForm.id_proveedor || ''} onChange={e => setModalItemForm(f => ({ ...f, id_proveedor: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500">
                      <option value="">Sin asignar</option>
                      {proveedores.map(p => <option key={`prov-${p.id}`} value={p.id}>{p.nombre}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Precio Lista</label>
                    <input type="number" value={modalItemForm.precio_publico} onChange={e => {
                      const v = e.target.value;
                      setModalItemForm(f => ({
                        ...f,
                        precio_publico: v,
                        precio_efectivo: v !== '' && !isNaN(parseFloat(v)) && parseFloat(v) > 0
                          ? String(redondearMiles(parseFloat(v) * (1 - descuentoEfectivo / 100)))
                          : f.precio_efectivo,
                      }));
                    }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500" min="0" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Precio Efectivo <span className="text-gray-400 font-normal">({descuentoEfectivo}% desc.)</span></label>
                    <input type="number" value={modalItemForm.precio_efectivo} onChange={e => setModalItemForm(f => ({ ...f, precio_efectivo: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500" min="0" placeholder="opcional" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Código EAN</label>
                    <input type="text" value={modalItemForm.codigo_ean || ''} onChange={e => setModalItemForm(f => ({ ...f, codigo_ean: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500" placeholder="Ej: 1234567890123" />
                  </div>
                  <div className="col-span-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={modalItemForm.sol || false} onChange={e => setModalItemForm(f => ({ ...f, sol: e.target.checked }))} />
                      <span className="font-medium text-gray-700">Es de Sol</span>
                    </label>
                  </div>

                  <div className="col-span-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={modalItemForm.en_ml || false} onChange={e => setModalItemForm(f => ({ ...f, en_ml: e.target.checked }))} />
                      <span className="font-medium text-gray-700">Publicado en ML</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Lente form */}
              {modalItemTipo === 'lente' && (
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Tipo visión <span className="text-red-500">*</span></label>
                    <select value={modalItemForm.tipo_vision} onChange={e => setModalItemForm(f => ({ ...f, tipo_vision: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500">
                      {['Monofocal','Bifocal','Multifocal'].map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Marca <span className="text-red-500">*</span></label>
                    <input type="text" value={modalItemForm.marca} onChange={e => setModalItemForm(f => ({ ...f, marca: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Color</label>
                    <input type="text" value={modalItemForm.color} onChange={e => setModalItemForm(f => ({ ...f, color: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Rango</label>
                    <input type="text" value={modalItemForm.rango} onChange={e => setModalItemForm(f => ({ ...f, rango: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Precio Lista</label>
                    <input type="number" value={modalItemForm.precio_publico} onChange={e => {
                      const v = e.target.value;
                      setModalItemForm(f => ({
                        ...f,
                        precio_publico: v,
                        precio_efectivo: v !== '' && !isNaN(parseFloat(v)) && parseFloat(v) > 0
                          ? String(redondearMiles(parseFloat(v) * (1 - descuentoEfectivo / 100)))
                          : f.precio_efectivo,
                      }));
                    }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500" min="0" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Precio Efectivo <span className="text-gray-400 font-normal">({descuentoEfectivo}% desc.)</span></label>
                    <input type="number" value={modalItemForm.precio_efectivo} onChange={e => setModalItemForm(f => ({ ...f, precio_efectivo: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500" min="0" placeholder="opcional" />
                  </div>
                  <div className="col-span-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={modalItemForm.son_esteticas ?? false} onChange={e => setModalItemForm(f => ({ ...f, son_esteticas: e.target.checked }))}
                        className="rounded border-gray-300" />
                      Estéticas
                    </label>
                  </div>
                </div>
              )}

              <div className="flex space-x-3">
                <button onClick={() => setModalItem(false)} className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancelar</button>
                <button onClick={handleGuardarItem} disabled={guardandoItem}
                  className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {guardandoItem ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Modal actualización masiva de precios ────────────────────── */}
        {modalMasivo && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={e => { if (e.target === e.currentTarget) setModalMasivo(false); }}
          >
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-6 border-b">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <ChartBarIcon className="h-5 w-5 text-amber-500" />Actualización masiva de precios
                </h3>
                <button onClick={() => setModalMasivo(false)} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                {/* Sección */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sección</label>
                  <div className="flex flex-wrap gap-2">
                    {[['cristales','Cristales'],['armazones','Armazones'],['lentes','Lentes'],['productos','Productos']].map(([k, lbl]) => (
                      <button key={k} onClick={() => { setMasivoSeccion(k); setMasivoMarca(''); setMasivoSeleccionados(new Set()); }}
                        className={`px-3 py-1.5 text-sm rounded-lg border-2 font-medium transition-all ${masivoSeccion === k ? 'bg-amber-100 text-amber-800 border-amber-300' : 'text-gray-600 border-transparent hover:bg-gray-100'}`}
                      >{lbl}</button>
                    ))}
                  </div>
                </div>

                {/* Modo */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Criterio de selección</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="radio" value="marca" checked={masivoModo === 'marca'} onChange={() => { setMasivoModo('marca'); setMasivoSeleccionados(new Set()); }}
                        className="accent-amber-500" />
                      Por marca
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="radio" value="seleccion" checked={masivoModo === 'seleccion'} onChange={() => setMasivoModo('seleccion')}
                        className="accent-amber-500" />
                      Selección manual
                    </label>
                  </div>
                </div>

                {/* Marca selector */}
                {masivoModo === 'marca' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Marca <span className="text-gray-400 font-normal">(vacío = todas)</span></label>
                    <select value={masivoMarca} onChange={e => setMasivoMarca(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400"
                    >
                      <option value="">Todas las marcas ({itemsParaMasivo.length} registros)</option>
                      {marcasMasivo.map(m => (
                        <option key={m} value={m}>{m} ({itemsParaMasivo.filter(i => i.marca === m).length})</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Tabla selección manual */}
                {masivoModo === 'seleccion' && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">{masivoSeleccionados.size} seleccionados de {itemsParaMasivo.length}</span>
                      <div className="flex gap-2">
                        <button onClick={seleccionarTodosMasivo} className="text-xs text-blue-600 hover:underline">Todos</button>
                        <button onClick={deseleccionarTodosMasivo} className="text-xs text-red-500 hover:underline">Ninguno</button>
                      </div>
                    </div>
                    <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                      <table className="w-full text-xs">
                        <tbody>
                          {itemsParaMasivo.map(item => (
                            <tr key={item.id} className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${masivoSeleccionados.has(item.id) ? 'bg-amber-50' : ''}`}
                              onClick={() => toggleSeleccionMasivo(item.id)}
                            >
                              <td className="px-3 py-2 w-8">
                                <input type="checkbox" checked={masivoSeleccionados.has(item.id)} onChange={() => {}} className="rounded" />
                              </td>
                              <td className="px-3 py-2 font-medium text-gray-900">{item.marca || item.tipo || '-'}</td>
                              <td className="px-3 py-2 text-gray-600">{item.modelo || item.producto || item.color || item.rango || ''}</td>
                              <td className="px-3 py-2 text-right text-gray-700">{formatearPrecio(item.precio_publico || item.precio_lista)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Parámetros del aumento */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Porcentaje (%)</label>
                    <div className="relative">
                      <input type="number" value={masivoPorcentaje} onChange={e => setMasivoPorcentaje(e.target.value)}
                        className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-amber-400"
                        placeholder="Ej: 15 o -10" step="0.1" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Positivo = aumento, negativo = descuento</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Aplicar a</label>
                    <select value={masivoTipoPrecio} onChange={e => setMasivoTipoPrecio(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400"
                    >
                      <option value="lista">Precio Lista</option>
                      <option value="efectivo">Precio Efectivo</option>
                      <option value="ambos">Ambos precios</option>
                    </select>
                  </div>
                </div>

                {/* Preview */}
                {masivoPorcentaje !== '' && !isNaN(parseFloat(masivoPorcentaje)) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                    Se actualizarán <strong>{masivoModo === 'seleccion' ? masivoSeleccionados.size : itemsFiltradosMasivo.length}</strong> registro/s
                    con un <strong>{parseFloat(masivoPorcentaje) > 0 ? 'aumento' : 'descuento'} del {Math.abs(parseFloat(masivoPorcentaje))}%</strong> en {masivoTipoPrecio === 'ambos' ? 'ambos precios' : `precio ${masivoTipoPrecio}`}.
                  </div>
                )}
              </div>

              <div className="flex space-x-3 p-6 border-t">
                <button onClick={() => setModalMasivo(false)} className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancelar</button>
                <button
                  onClick={handleAplicarMasivo}
                  disabled={aplicandoMasivo || !masivoPorcentaje || isNaN(parseFloat(masivoPorcentaje)) || parseFloat(masivoPorcentaje) === 0 || (masivoModo === 'seleccion' && masivoSeleccionados.size === 0)}
                  className="flex-1 px-4 py-2 text-white bg-amber-500 rounded-lg hover:bg-amber-600 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
                >
                  {aplicandoMasivo ? 'Aplicando...' : `Aplicar a ${masivoModo === 'seleccion' ? masivoSeleccionados.size : itemsFiltradosMasivo.length} registro/s`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Modal colores de marcas ──────────────────────────────────── */}
        {modalColores && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={e => { if (e.target === e.currentTarget) { setModalColores(false); setColorEditando(null); setColorForm({ marca: '', presetKey: 'azul-oscuro' }); } }}
          >
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <SwatchIcon className="h-5 w-5 text-indigo-500" />Colores por marca
                </h3>
                <button onClick={() => { setModalColores(false); setColorEditando(null); setColorForm({ marca: '', presetKey: 'azul-oscuro' }); }}
                  className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
              </div>

              {/* Lista de marcas configuradas */}
              <div className="overflow-y-auto flex-1 p-5 space-y-2">
                {Object.keys(marcaColores).length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4">No hay marcas configuradas todavía.</p>
                )}
                {Object.entries(marcaColores).map(([marca, colores]) => {
                  const preset = COLOR_PRESETS.find(p => p.key === colores.preset);
                  return (
                    <div key={marca} className={`flex items-center justify-between px-4 py-2.5 rounded-lg border ${colores.even} border-gray-200`}>
                      <div className="flex items-center gap-3">
                        {preset && <span className={`inline-block w-3 h-3 rounded-full ${preset.dot}`} />}
                        <span className="text-sm font-semibold text-gray-800 capitalize">{marca}</span>
                        {preset && <span className="text-xs text-gray-500">{preset.label}</span>}
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => handleEditarColor(marca)}
                          className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex items-center gap-1">
                          <PencilIcon className="h-3 w-3" />Editar
                        </button>
                        <button onClick={() => handleEliminarColor(marca)}
                          className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 flex items-center gap-1">
                          <TrashIcon className="h-3 w-3" />Quitar
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Formulario agregar / editar */}
              <div className="border-t p-5 space-y-3 bg-gray-50">
                <p className="text-sm font-medium text-gray-700">{colorEditando ? `Editando: ${colorEditando}` : 'Agregar nueva marca'}</p>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={colorForm.marca}
                    onChange={e => setColorForm(f => ({ ...f, marca: e.target.value }))}
                    placeholder="Nombre de marca"
                    disabled={!!colorEditando}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-400 disabled:bg-gray-100 disabled:text-gray-500"
                  />
                  <select
                    value={colorForm.presetKey}
                    onChange={e => setColorForm(f => ({ ...f, presetKey: e.target.value }))}
                    className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-400"
                  >
                    {COLOR_PRESETS.map(p => (
                      <option key={p.key} value={p.key}>{p.label}</option>
                    ))}
                  </select>
                </div>
                {(() => {
                  const preset = COLOR_PRESETS.find(p => p.key === colorForm.presetKey);
                  return preset ? (
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${preset.even} text-sm text-gray-700`}>
                      <span className={`inline-block w-3 h-3 rounded-full ${preset.dot}`} />
                      Vista previa — filas pares: <strong className="ml-1">{preset.label}</strong>
                    </div>
                  ) : null;
                })()}
                <div className="flex gap-2">
                  {colorEditando && (
                    <button onClick={() => { setColorEditando(null); setColorForm({ marca: '', presetKey: 'azul-oscuro' }); }}
                      className="flex-1 px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">
                      Cancelar
                    </button>
                  )}
                  <button
                    onClick={handleGuardarColor}
                    disabled={!colorForm.marca.trim()}
                    className="flex-1 px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium"
                  >
                    {colorEditando ? 'Actualizar' : 'Agregar'}
                  </button>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <button
                    onClick={handleDetectarDuplicados}
                    disabled={cargandoDuplicados}
                    className="px-4 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {cargandoDuplicados ? 'Detectando...' : 'Detectar duplicados'}
                  </button>
                  <button
                    onClick={() => setDuplicadosCatalogo([])}
                    className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  >
                    Limpiar resultado
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Modal administración de catálogos normalizados ───────────── */}
        {modalCatalogos && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center overflow-y-auto z-50 px-4 pt-24 pb-6"
            onClick={e => {
              if (e.target === e.currentTarget) {
                setCatalogoBusqueda('');
                setCatalogoEstadoFiltro('todos');
                setNuevoCatalogoNombre('');
                setNuevoLaboratorioDireccion('');
                setNuevoLaboratorioTelefonos('');
                setNuevoMaterialRango('');
                setModalCatalogos(false);
              }
            }}
          >
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[calc(100vh-7rem)] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <BeakerIcon className="h-5 w-5 text-blue-600" />Herramientas de cristales: catálogos
                </h3>
                <button onClick={() => {
                  setCatalogoBusqueda('');
                  setCatalogoEstadoFiltro('todos');
                  setNuevoCatalogoNombre('');
                  setNuevoLaboratorioDireccion('');
                  setNuevoLaboratorioTelefonos('');
                  setNuevoMaterialRango('');
                  setModalCatalogos(false);
                }} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
              </div>

              <div className="p-5 space-y-4 overflow-y-auto flex-1">
                <div className="flex flex-wrap gap-2">
                  {catalogoTipos.map(t => (
                    <button
                      key={t.key}
                      onClick={() => setCatalogoActivo(t.key)}
                      className={`px-3 py-1.5 text-sm rounded-lg border-2 font-medium ${
                        catalogoActivo === t.key
                          ? 'bg-blue-100 text-blue-700 border-blue-300'
                          : 'text-gray-600 border-transparent hover:bg-gray-100'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={nuevoCatalogoNombre}
                    onChange={e => setNuevoCatalogoNombre(e.target.value)}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                    placeholder="Nuevo nombre"
                  />
                  <button
                    onClick={handleCrearCatalogo}
                    disabled={guardandoCatalogo || !nuevoCatalogoNombre.trim()}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {guardandoCatalogo ? 'Guardando...' : 'Agregar'}
                  </button>
                </div>

                {catalogoActivo === 'laboratorios' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={nuevoLaboratorioDireccion}
                      onChange={e => setNuevoLaboratorioDireccion(e.target.value)}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                      placeholder="Dirección del laboratorio"
                    />
                    <input
                      type="text"
                      value={nuevoLaboratorioTelefonos}
                      onChange={e => setNuevoLaboratorioTelefonos(e.target.value)}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                      placeholder="Teléfonos (coma o punto y coma)"
                    />
                  </div>
                )}

                {catalogoActivo === 'materiales_cristales' && (
                  <div className="grid grid-cols-1 gap-2">
                    <input
                      type="text"
                      value={nuevoMaterialRango}
                      onChange={e => setNuevoMaterialRango(e.target.value)}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                      placeholder="Rango del material"
                    />
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    type="text"
                    value={catalogoBusqueda}
                    onChange={e => setCatalogoBusqueda(e.target.value)}
                    placeholder="Buscar por nombre..."
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                  />
                  <select
                    value={catalogoEstadoFiltro}
                    onChange={e => setCatalogoEstadoFiltro(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400"
                  >
                    <option value="todos">Todos</option>
                    <option value="activos">Sólo activos</option>
                    <option value="inactivos">Sólo inactivos</option>
                  </select>
                </div>

                <div className="flex items-center justify-between gap-2">
                  <button
                    onClick={handleDetectarDuplicados}
                    disabled={cargandoDuplicados}
                    className="px-4 py-2 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {cargandoDuplicados ? 'Detectando...' : 'Detectar duplicados'}
                  </button>
                </div>

                <div className="border border-gray-200 rounded-lg divide-y max-h-[380px] overflow-y-auto">
                  {itemsCatalogoActivo.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-6">No hay ítems cargados para este catálogo.</p>
                  )}
                  {itemsCatalogoActivo.map(item => (
                    <div key={item.id} className="px-4 py-3 flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{item.nombre}</p>
                        {catalogoActivo === 'laboratorios' && item.direccion && (
                          <p className="text-xs text-gray-500">Direccion: {item.direccion}</p>
                        )}
                        {catalogoActivo === 'laboratorios' && Array.isArray(item.telefonos) && item.telefonos.length > 0 && (
                          <p className="text-xs text-gray-500">Telefonos: {item.telefonos.join(' | ')}</p>
                        )}
                        {catalogoActivo === 'materiales_cristales' && item.rango && (
                          <p className="text-xs text-gray-500">Rango: {item.rango}</p>
                        )}
                        <p className={`text-xs ${item.activo ? 'text-green-600' : 'text-gray-400'}`}>{item.activo ? 'Activo' : 'Inactivo'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {(catalogoActivo === 'laboratorios' || catalogoActivo === 'materiales_cristales') && (
                          <button
                            onClick={() => catalogoActivo === 'laboratorios' ? handleEditarLaboratorioDatos(item) : handleEditarMaterialDatos(item)}
                            className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                          >
                            Editar datos
                          </button>
                        )}
                        <button
                          onClick={() => handleRenombrarCatalogo(item)}
                          className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                        >
                          Renombrar
                        </button>
                        <button
                          onClick={() => handleToggleActivoCatalogo(item)}
                          className={`px-2 py-1 text-xs rounded ${item.activo ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}
                        >
                          {item.activo ? 'Desactivar' : 'Reactivar'}
                        </button>
                        <button
                          onClick={() => handleEliminarCatalogoItem(item)}
                          className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700"
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {duplicadosCatalogo.length > 0 && (
                  <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="font-semibold text-amber-900">Duplicados detectados</h4>
                      <span className="text-xs text-amber-700">{duplicadosCatalogo.length} grupos</span>
                    </div>
                    <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                      {duplicadosCatalogo.map((grupo) => (
                        <div key={grupo.nombre_normalizado} className="bg-white border border-amber-200 rounded-lg p-3">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <div>
                              <p className="text-sm font-semibold text-gray-900">{grupo.nombre_normalizado}</p>
                              <p className="text-xs text-gray-500">
                                {grupo.cantidad} registros · {grupo.activos} activos · {grupo.inactivos} inactivos
                              </p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {grupo.items.map((item) => (
                              <span
                                key={item.id}
                                className={`px-2 py-1 text-xs rounded-full border ${item.activo ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}
                              >
                                {item.nombre}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-amber-800">
                      Estos grupos te sirven para decidir un valor canonico y fusionar el resto mas adelante, sin revisar uno por uno.
                    </p>
                  </div>
                )}

                <p className="text-xs text-gray-500">
                  Los nuevos cristales y armazones van a reutilizar estos valores y evitar duplicados por óptica.
                </p>
              </div>
            </div>
          </div>
        )}

        <datalist id="catalogo-laboratorios">
          {opcionesCatalogoActivas.laboratorios.map(nombre => <option key={`lab-${nombre}`} value={nombre} />)}
        </datalist>
        <datalist id="catalogo-marcas-cristales">
          {opcionesCatalogoActivas.marcasCristales.map(nombre => <option key={`mc-${nombre}`} value={nombre} />)}
        </datalist>
        <datalist id="catalogo-productos-cristales">
          {opcionesCatalogoActivas.productosCristales.map(nombre => <option key={`pc-${nombre}`} value={nombre} />)}
        </datalist>
        <datalist id="catalogo-materiales-cristales">
          {opcionesCatalogoActivas.materialesCristales.map(nombre => <option key={`mt-${nombre}`} value={nombre} />)}
        </datalist>
        <datalist id="catalogo-marcas-armazones">
          {opcionesCatalogoActivas.marcasArmazones.map(nombre => <option key={`ma-${nombre}`} value={nombre} />)}
        </datalist>

        {/* ── Modal de Mercado Pago ── */}
        <MercadoPagoConfigModal
          isOpen={modalMPOpen}
          onClose={() => setModalMPOpen(false)}
        />

        {/* ── Modal de Políticas Comerciales y Descuentos por Marca/Tipo ── */}
        <ModalPoliticaDescuentos
          isOpen={modalPoliticaOpen}
          onClose={() => setModalPoliticaOpen(false)}
          onSaved={(nuevaPol) => {
            if (nuevaPol?.general?.receta?.efectivo != null) {
              setDescuentoEfectivo(nuevaPol.general.receta.efectivo);
              localStorage.setItem(_descuentoKey, String(nuevaPol.general.receta.efectivo));
            }
            if (nuevaPol?.general?.sol?.efectivo != null) {
              setDescuentoArmazones(nuevaPol.general.sol.efectivo);
              localStorage.setItem(_descuentoArmazonesKey, String(nuevaPol.general.sol.efectivo));
            }
          }}
        />

        {/* ── Modal imprimir / exportar ────────────────────────────────── */}
        {modalImprimir && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={e => { if (e.target === e.currentTarget) setModalImprimir(false); }}
          >
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <PrinterIcon className="h-5 w-5 text-green-600" />
                  Exportar / Imprimir
                </h3>
                <button onClick={() => setModalImprimir(false)} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
              </div>

              <div className="p-5 space-y-3">
                <p className="text-sm text-gray-600">Seleccioná las secciones que querés incluir:</p>

                <div className="space-y-2">
                  {[
                    { key: 'monofocal',   label: 'Cristales Monofocales',  count: cristalesMonofocales.length,    dot: '#3b82f6' },
                    { key: 'bifocal',     label: 'Cristales Bifocales',     count: cristalesBifocales.length,      dot: '#a855f7' },
                    { key: 'multifocal',  label: 'Cristales Multifocales',  count: cristalesMultifocales.length,   dot: '#10b981' },
                    { key: 'ocupacional', label: 'Cristales Ocupacionales', count: cristalesOcupacionales.length,  dot: '#06b6d4' },
                    { key: 'lentes',      label: 'Lentes de Contacto',      count: lentesContactoFiltrados.length, dot: '#14b8a6' },
                    { key: 'armazones',   label: 'Armazones',               count: armazonesFiltrados.length,      dot: '#f97316' },
                    { key: 'productos',   label: 'Productos / Fornituras',   count: productosFiltrados.length,      dot: '#6366f1' },
                  ].map(({ key, label, count, dot }) => {
                    const sinDatos = count === 0;
                    const checked = imprimirOpciones[key] && !sinDatos;
                    return (
                      <label key={key} className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border-2 transition-all select-none ${
                        sinDatos
                          ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                          : checked
                          ? 'border-green-200 bg-green-50 cursor-pointer'
                          : 'border-gray-200 hover:border-gray-300 bg-white cursor-pointer'
                      }`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={sinDatos}
                          onChange={e => setImprimirOpciones(o => ({ ...o, [key]: e.target.checked }))}
                          className="rounded border-gray-300 accent-green-600"
                        />
                        <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ background: dot }} />
                        <span className={`flex-1 text-sm font-medium ${sinDatos ? 'text-gray-400' : 'text-gray-800'}`}>{label}</span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${sinDatos ? 'bg-gray-100 text-gray-400' : 'bg-gray-100 text-gray-600'}`}>
                          {count} {count === 1 ? 'ítem' : 'ítems'}
                        </span>
                      </label>
                    );
                  })}
                </div>

                {(() => {
                  const total = [
                    imprimirOpciones.monofocal   ? cristalesMonofocales.length    : 0,
                    imprimirOpciones.bifocal     ? cristalesBifocales.length      : 0,
                    imprimirOpciones.multifocal  ? cristalesMultifocales.length   : 0,
                    imprimirOpciones.ocupacional ? cristalesOcupacionales.length  : 0,
                    imprimirOpciones.lentes      ? lentesContactoFiltrados.length : 0,
                    imprimirOpciones.armazones   ? armazonesFiltrados.length      : 0,
                    imprimirOpciones.productos   ? productosFiltrados.length      : 0,
                  ].reduce((s, n) => s + n, 0);
                  return (
                    <div className="bg-gray-50 rounded-lg px-4 py-2.5 flex items-center justify-between text-sm border border-gray-200">
                      <span className="text-gray-600">Total a imprimir:</span>
                      <span className="font-bold text-gray-900">{total} ítems</span>
                    </div>
                  );
                })()}
              </div>

              <div className="flex gap-3 p-5 border-t">
                <button onClick={() => setModalImprimir(false)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">
                  Cancelar
                </button>
                <button
                  onClick={() => { setModalImprimir(false); handleImprimir(imprimirOpciones); }}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 font-medium"
                >
                  <PrinterIcon className="h-4 w-4" />Imprimir
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );

};

export default ListaPrecios;
